import Packages from '@/common/utils/en-packages'
import { platformIsInstanceOf } from '@/common/utils/platformIsInstanceOf'
import Convert from '@/common/utils/evals/convert'
//import Messages from '@/common/utils/evals/messages'
import ProgressBar from '@/common/utils/evals/progressBar'
import { analyzeAndInjectAsync } from '@/common/utils/evals/script-async-analyzer'
import { transformJavaLengthCalls } from '@/common/utils/evals/script-java-length-transform'
import {
  pickDocumentIdentityFromSnapshot,
  resolveFormFunctionsForEnRun,
  scriptHasAsyncOperations,
} from '@/common/utils/enFormFunctions'
import { javaLength } from '@/common/utils/javaLength'
import { prependFormFunctionsUnlessPresent } from '@/common/utils/formRootOnLoad'
import { messagesService } from '@/services/messages/messages.service'
import { MiddleExecutionRequiredError } from '@/services/parse/MiddleExecutionRequiredError'
import parseService from '@/services/parse/parse.service'
import { buildRuntimeGlobals, assignClientImportWindowGlobals } from '@/services/script/runtime-window-globals'
import { evaluatePlatformScript } from '@/common/utils/evaluatePlatformScript'
import { installPlatformScriptConsole, reportPlatformScriptAlert } from '@/common/utils/scriptConsole'
import { extractProtocolAndDomain } from '@/common/utils/extractProtocolAndDomain'
import { platformEach } from '@/common/utils/platformEach'
import { fileService } from '@/services/file/file.service'
import { installJavaUtilShim } from './javaUtilShim'
import UniData from './UniData'
import UniDataTable from './UniDataTable'
import UniDataTools from './UniDataTools'
import {
  fieldEntryHasPresentableValue,
  fieldEntryIsUniDataTable,
  fieldEntryLooksEmptyIncoming,
  shouldPreserveFieldEntryOnEmptyIncoming,
} from '@/common/utils/changedDataFieldMerge'
import { isElementScopedFieldKey, scopeUniDataToElementField } from '@/common/utils/elementScopedUniData'
import { normalizeElementCollectedDataForExport, applyDateRangeToClientDataFieldsFromRegistry } from '@/common/utils/elementGetDataExport'
import { normalizeReloadPanelTableField } from '@/common/utils/applyReloadPanelFieldsToChangedData'
import { prunePutFullDataCollectedFields } from '@/common/utils/elementPutFullDataExport'
import { parseComboBoxIdFieldPairsFromElement } from '@/common/utils/comboBoxSwingFields'
import { mergeFieldMaps } from '@/common/utils/jsonUniData'
import { duringOwnedElementReload } from '@/common/utils/ownedElementReload'
import {
  FOREGROUND_COLOR_ATTRIBUTE,
  rememberFieldForeground,
} from '@/common/utils/fieldForeground'
import { eventsLayoutReloadFormFields } from '@/common/utils/documentTransferIdentity'
import { isEventsLayoutAction } from '@/common/utils/reloadFormRequestFields'
import { unwrapScriptValue } from '@/common/utils/wrapScriptValue'
import { convertRowDataToFieldsForScript } from '@/common/utils/rowDataUtils'
import { findLayoutPageTabByVisibilityName } from '@/common/utils/pageTabMeta'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'
import { wrapScriptValue } from '@/common/utils/wrapScriptValue'
import { emptyUniDataTableField } from '@/common/utils/treeTableReloadNodePayload'
import {
  collectTilesCloneWidgetFieldNames,
  filterLiveOverridesToCurrentTilesRowPrefix,
  overlayTilesCloneWidgetFieldsFromRow,
  overlayTilesRowIdentityOnFieldMap,
  pickTilesRowIdentityFields,
  pickTilesRowIdentityFromCollectedTable,
  type SwingTilesRowScopeLike,
} from '@/common/utils/tilesRowPanelData'
import {
  applyFileAttachmentsToClientDataFieldsFromRegistry,
  emptyFileAttachmentsUniDataTable,
  fileItemsToUniDataTable,
  isFileAttachmentFieldInForm,
  pickPreferredFileAttachmentsField,
  uniDataTableToFileItems,
} from '@/common/utils/fileAttachmentsUniData'
import { resolveFileAttachmentsPutFullDataElement } from '@/services/action/collectFileAttachmentsPutFullData'
import {
  applySynthActionTableLeafCollect,
  resolveFormTableSelectionsForSynthCollect,
} from '@/common/utils/applyPutFormDataSynthCollect'
import {
  applyFormIdentificatorAndListboxGetDataToFields,
  isHiddenTableLikeElement,
} from '@/common/utils/panelGetDataCollect'
import { toMultiSelectTypedValue } from '@/common/utils/normalizeMultiselectTableValue'
import {
  TEXT_EDITED_LISTENER_EVENT,
  isPlainTextFieldType,
  resolveFormElementTypeById,
} from '@/common/utils/textEditedListener'
import { resolvePanelIdByJsName } from '@/common/utils/collectOnServerEventScripts'
import {
  collectLayoutFieldNames,
  isLayoutScopedFieldKey,
  isIdentificatorLayoutFieldName,
} from '@/common/utils/layoutScopedClientFields'
import { registryElementMatchesRequestedForm } from '@/common/utils/liveFormFieldOverrides'
import {
  classifySetEditableName,
  resolvePanelXmlDefaultEditable,
  shouldCascadePanelEditable,
} from '@/common/utils/panelEditableCascade'
import { normalizeFormErrorMessage } from '@/common/utils/normalizeFormErrorMessage'
import { shouldDeferOverlayElementRefill } from '@/common/utils/formOverlayPageScope'
import {
  collectRegisteredElementsByName,
  resolveRegisteredElementByName,
} from '@/common/utils/resolveRegisteredElementByName'

declare global {
  interface Array<T> {
    size(): number
  }
  interface Object {
    equals(other: unknown): boolean
  }
  interface String {
    contains(s: string): boolean
  }
}

interface Entity {
  refill?: (data?: any) => void
  getType?: () => string | undefined
  getSuffixes?: () => string[]
  reload?: (data: any, options?: { applyResult?: boolean; source?: string }) => void
  reloadNode?: (rowNum?: unknown, needReloadParent?: unknown, selectedNodeData?: unknown) => Promise<any>
  buildReloadNodePayload?: (rowNum: number, needReloadParent: boolean) => Record<string, any>
  invokeEvent?: (eventName: string, data?: any) => void
  verify?: () => boolean
  receiveFocus?: () => void
  scrollIntoViewElement?: () => boolean | void
  setVisible?: (isVisible: boolean) => void
  getSVGContainer?: () => unknown
  setEditable?: (isEditable: boolean) => void
  setEnable?: (enabled: boolean) => void
  /** Swing `ActionVisualEntity.setEnabled`; web buttons store this as `setEnable`. */
  setEnabled?: (enabled: boolean) => void
  /** Swing `ActionVisualEntity.setTitle`; web buttons store the label override. */
  setTitle?: (title: string) => void
  getVisualContainer?: () => {
    setEnable?: (enabled: boolean) => void
    setEnabled?: (enabled: boolean) => void
    setTitle?: (title: string) => void
  }
  setHasEditor?: (hasEditor: boolean) => void
  setError?: (message: string | null) => void
  removeTextFieldBorder?: () => void
  setCursorPositionToEnd?: () => void
  openSuggester?: () => void
  setVerifyRegexp?: () => void
  name?: string
  fieldName?: string
  elementId?: string | number
  formId?: string
  schemaFormId?: string
  panelId?: string
  elementType?: string
  registeredValue?: unknown
  getData?: (arg?: unknown) => unknown
  getFullData?: () => unknown
  /** Close inline/modal R7 and wait awaiting_ws → staged before putFormData / putFullData FA upload. */
  prepareForFormSave?: () => void | Promise<void>
  getName?: () => string
  getActionByColumnName?: (rowIndex: unknown, columnName: unknown) => unknown
  ctx?: { id?: string; tilesRowScope?: SwingTilesRowScopeLike }
  doNotSerialize?: boolean
  pageId?: string
  /** Совместимость с en.getEntity('action', id).getAction().doAction(...) в web_client_exec */
  getAction?: () => Entity
  doAction?: (newWindow?: boolean, debug?: boolean) => Promise<unknown>
  /** XML TABLE/EDITABLE checkbox flag (registry / putFormData slice). */
  withCheckBoxes?: boolean | { _text?: string }
}

interface EnConstructorParams {
  controls: any
  elements: Record<string, Entity>
  /** Если задан, при каждом обращении к элементам используется актуальный список (для скриптов onLoad/listeners). */
  getElements?: () => Record<string, Entity>
  actions: Record<string, Entity>
  pageData: any
  formId?: number
  panelId?: number
  pageId?: number
  elementId?: number
  asyncImports?: any
}

/** Swing scripts call `action.setEnabled`; web hooks are `setEnable`. */
function aliasActionSetEnabled(action: Entity | undefined): Entity | undefined {
  if (!action) return action
  const row = action as Entity & {
    getVisualContainer?: () => {
      setEnable?: (enabled: boolean) => void
      setEnabled?: (enabled: boolean) => void
    }
  }
  if (typeof action.setEnabled !== 'function' && typeof action.setEnable === 'function') {
    action.setEnabled = action.setEnable
  }
  const getVisualContainer = row.getVisualContainer
  if (typeof getVisualContainer === 'function') {
    const original = getVisualContainer
    if (!(original as { __setEnabledAliased?: boolean }).__setEnabledAliased) {
      const wrapped = () => {
        const container = original.call(action)
        if (
          container &&
          typeof container.setEnabled !== 'function' &&
          typeof container.setEnable === 'function'
        ) {
          container.setEnabled = container.setEnable
        }
        return container
      }
      ;(wrapped as { __setEnabledAliased?: boolean }).__setEnabledAliased = true
      row.getVisualContainer = wrapped
    }
  }
  return action
}

/** Typed field map from POST `/platform/reload/form` (JSContext setData). */
export function pickReloadFormFieldsFromResult(result: unknown): Record<string, unknown> | null {
  if (!result || typeof result !== 'object' || Array.isArray(result)) return null
  const rec = result as Record<string, unknown>
  const nested = rec.fields
  if (!nested || typeof nested !== 'object' || Array.isArray(nested)) return null
  const nestedRec = nested as Record<string, unknown>
  const inner = nestedRec.fields
  if (inner && typeof inner === 'object' && !Array.isArray(inner)) {
    return inner as Record<string, unknown>
  }
  if (!Object.prototype.hasOwnProperty.call(nestedRec, 'fields')) {
    return nestedRec
  }
  return null
}

/**
 * Класс для выполнения прикладного кода платформы.
 * Вызов `getEntity('action', id).getAction().doAction(...)` (аналог вложенного doAction) поддерживается только при исполнении в браузере через `web_client_exec` / `executeClientCode`; см. `ActionService.invokeNestedElementActionFromClientScript`.
 */
export default class En {
  private data: UniData
  private controls: any
  private elements: Record<string, Entity>
  private getElementsRef?: () => Record<string, Entity>
  private actions: Record<string, Entity>
  private pageData: any

  private formId: number = -1
  private panelId: number = -1
  private pageId: number = -1
  private elementId: number = -1
  private pageDataSlotId: string = ''
  /** sessionId WS-события onServerEvent — reloadPanel должен идти в тот же core. */
  private serverEventCoreId: string | null = null
  /**
   * Форма, на которую подписан текущий onServerEvent.
   * Общий En могут переключить на диалог между await reloadPanel.
   */
  private serverEventFormPin: {
    pageDataSlotId: string
    formId: number
    panelId: number
    pageData: unknown
    sessionId: string | null
  } | null = null
  private tilesRowScope: SwingTilesRowScopeLike | null = null
  /** Поля, уже записанные `putFullData` в этом En — пустой checkbox-срез putFormData их не затирает. */
  private _putFullDataKeys = new Set<string>()
  /** Live container editable for 2-arg setEditable fallback (no formProvider). */
  private containerEditableById: Record<string, boolean> = {}

  private properties: Record<string, any> = {}
  private actionData: Record<string, any> = {}
  /** Row from the element action. reloadForm must not turn it into the form document. */
  private reloadFormSelectedData: unknown = null
  /**
   * Action whose script called reloadForm. Kept off actionData: _setActionData
   * overlays the selected row, and chainCalled replaces actionData with that row.
   */
  private reloadFormActionLayout: unknown = null
  /** Per-click token so one in-flight action cannot clear another's setContext. */
  private actionContextSeq = 0
  private inFlightActionContexts = 0
  private contextSetTokens = new Set<number>()
  /** Nested en.run (onLoad during reloadForm) must not clear the outer script flag. */
  private scriptRunDepth = 0

  private asyncImports: any

  constructor({
                controls,
                elements,
                getElements,
                actions,
                pageData,
                formId,
                panelId,
                pageId,
                elementId,
                asyncImports,
              }: EnConstructorParams) {
    this.data = new UniData({})
    this.controls = controls
    this.elements = elements
    this.getElementsRef = getElements
    this.actions = actions
    this.pageData = pageData
    this.asyncImports = asyncImports ?? {}

    // Set form/panel/element IDs if provided
    if (formId !== undefined) this.formId = formId
    if (panelId !== undefined) this.panelId = panelId
    if (pageId !== undefined) this.pageId = pageId
    if (elementId !== undefined) this.elementId = elementId

    this._extendStringPrototype()
    this._addJavaLikeCompatibility()
  }

  /** Текущая карта элементов: из геттера, если передан, иначе из this.elements. */
  private _getElements(): Record<string, Entity> {
    if (this.getElementsRef) {
      const live = this.getElementsRef()
      if (live && typeof live === 'object') return live
    }
    const fromControls = (this.controls as { getElements?: () => Record<string, Entity> })
      ?.getElements?.()
    if (fromControls && typeof fromControls === 'object') return fromControls
    return this.elements
  }

  private _getElementsByKey(): Record<string, Entity> | undefined {
    const ctrl = this.controls as {
      getElementsByKey?: () => Record<string, Entity>
      formProvider?: { getElementsByKey?: () => Record<string, Entity> }
    }
    return ctrl?.getElementsByKey?.() ?? ctrl?.formProvider?.getElementsByKey?.()
  }

  private _elementLookupExtraMaps(): Array<Record<string, Entity> | undefined> {
    const byKey = this._getElementsByKey()
    return byKey ? [byKey] : []
  }

  private _resolveElementEntity(name: string | number): Entity | undefined {
    return resolveRegisteredElementByName(
      this._getElements(),
      name,
      this.formId,
      this._elementLookupExtraMaps(),
    )
  }

  private _collectElementEntities(name: string | number): Entity[] {
    return collectRegisteredElementsByName(
      this._getElements(),
      name,
      this.formId,
      this._elementLookupExtraMaps(),
    )
  }

  private _registeredElementType(el: Entity | undefined): string {
    if (!el) return ''
    const raw =
      el.elementType ??
      (typeof el.getType === 'function' ? el.getType() : undefined)
    return String(raw ?? '').toUpperCase()
  }

  /**
   * Очищает скрипт от строк importClass, которые специфичны для Rhino
   */
  private _removeImportClassStatements(script: string): string {
    // Удаляем строки вида: importClass(Packages.ru.esoft.fatclient.tools.ProgressBar);
    // или: importClass(Packages.ru.esoft.fatclient.tools.Messages);
    if (!script) {
      return ''
    }
    const importClassRegex = /importClass\s*\([^)]+\)\s*;?\s*/g
    return script.replace(importClassRegex, '')
  }

  /**
   * Выполнить код и вернуть его результат
   * Поддерживает как синхронный, так и асинхронный код
   */
  async run(cmd: string = '', scriptContextToken?: number): Promise<any> {
    const outerScript = this.scriptRunDepth === 0
    this.scriptRunDepth += 1
    const previousEn = Object.getOwnPropertyDescriptor(window, 'en') ?? null
    const previousData = Object.getOwnPropertyDescriptor(window, 'data') ?? null
    try {
      if (
        outerScript &&
        this.inFlightActionContexts <= 1 &&
        (scriptContextToken == null || !this.contextSetTokens.has(scriptContextToken))
      ) {
        this.properties.isContextSet = false
      }
    this._addJavaLikeCompatibility()
    this.getData().ensureTableValuesHaveSize()
    const common = resolveFormFunctionsForEnRun(this.pageData, this.formId, this.pageDataSlotId)

    // Очищаем скрипт от importClass перед выполнением
    const cleanedCmd = this._removeImportClassStatements(cmd)
    const cleanedCommon = this._removeImportClassStatements(common)

    let result: any = {}

    const processedCmdRaw = parseService.getCommand(
      prependFormFunctionsUnlessPresent(cleanedCommon, cleanedCmd),
    )
    const withJavaLength = transformJavaLengthCalls(processedCmdRaw)

    // поместить в глобальную область видимости необходимые переменные
    this._setupGlobalScope()

    const processedCmd = analyzeAndInjectAsync(withJavaLength, this.asyncImports)
    // Literal token: a shared currentScriptToken is wrong once two runs await.
    const scriptForEval = this._rewriteSetContextCalls(processedCmd, scriptContextToken)
    // нужно особенно для продакшн версии
    const runtimeGlobals = buildRuntimeGlobals()
    const codeToEval = runtimeGlobals + scriptForEval

    // console.log('this', window)
    // console.log('asyncImports', this.asyncImports, processedCmd)

    const toNumberedCode = (source: string) =>
      source
        .split('\n')
        .map((line, index) => `${index + 1}`.padStart(4, ' ') + ': ' + line)
        .join('\n')

    const getEvalLineFromError = (error: unknown): number | null => {
      const stack = error instanceof Error ? error.stack ?? '' : String(error ?? '')
      const match = stack.match(/<anonymous>:(\d+):\d+/)
      if (!match) return null
      const line = Number(match[1])
      return Number.isFinite(line) ? line : null
    }

    const buildEvalExcerpt = (source: string, lineNumber: number | null, radius = 4): string => {
      const lines = source.split('\n')
      if (!lineNumber || lineNumber < 1 || lineNumber > lines.length) {
        return toNumberedCode(source)
      }
      const start = Math.max(1, lineNumber - radius)
      const end = Math.min(lines.length, lineNumber + radius)
      return lines
        .slice(start - 1, end)
        .map((line, idx) => {
          const current = start + idx
          const prefix = current === lineNumber ? '>>' : '  '
          return `${prefix}${String(current).padStart(4, ' ')}: ${line}`
        })
        .join('\n')
    }

    try {
      const hasAsyncOps = scriptHasAsyncOperations(scriptForEval)
      // Non-strict Function (не direct eval из ESM): Rhino-parity для `pdPolicy = 0` без var.
      result = await evaluatePlatformScript(codeToEval, {async: hasAsyncOps})
    } catch (error) {
      if (!(error instanceof MiddleExecutionRequiredError)) {
        console.error('Evaluation error: ', error)
        const evalLine = getEvalLineFromError(error)
        console.error(
          '[En.run] Evaluated code excerpt:\n' + buildEvalExcerpt(codeToEval, evalLine),
        )
      }
      throw error
    }

    return result
    } finally {
      this.scriptRunDepth = Math.max(0, this.scriptRunDepth - 1)
      this._restoreWindowBinding('en', previousEn)
      this._restoreWindowBinding('data', previousData)
    }
  }

  /** _setupGlobalScope replaces window.data with a getter-only property. */
  private _restoreWindowBinding(key: 'en' | 'data', desc: PropertyDescriptor | null): void {
    try {
      if (!desc) {
        delete (window as any)[key]
        return
      }
      Object.defineProperty(window, key, desc)
    } catch {
      // A host getter-only binding must not fail the script that just finished.
    }
  }

  /** Вспомогательные функции */

  private _extendStringPrototype(): void {
    String.prototype.isEmpty = function () {
      return !this
    }
    String.prototype.equals = function (str) {
      return String(this ?? '') === String(str ?? '')
    }
    if (typeof String.prototype.contains !== 'function') {
      String.prototype.contains = function (this: string, s: string) {
        return String(this).indexOf(s) !== -1
      }
    }
  }

  /**
   * Adds Java/Rhino-like APIs for backend script compatibility.
   */
  private _addJavaLikeCompatibility(): void {
    if (typeof Array.prototype.size !== 'function') {
      Object.defineProperty(Array.prototype, 'size', {
        value: function () { return this.length },
        writable: true,
        configurable: true,
      })
    }
    Object.defineProperty(Object.prototype, 'equals', {
      value: function (this: unknown, other: unknown) {
        const unwrapComparable = (value: unknown): unknown => {
          if (value instanceof Number || value instanceof String || value instanceof Boolean) {
            return value.valueOf()
          }
          if (
            value &&
            typeof value === 'object' &&
            'value' in (value as Record<string, unknown>) &&
            Object.keys(value as Record<string, unknown>).some((key) => key === 'value' || key === 'type')
          ) {
            return (value as Record<string, unknown>).value
          }
          return value
        }

        const left = unwrapComparable(this)
        const right = unwrapComparable(other)

        if (left === right) return true
        if (left == null || right == null) return false
        if (typeof left !== 'object' || typeof right !== 'object') return left === right
        return JSON.stringify(left) === JSON.stringify(right)
      },
      writable: true,
      configurable: true,
      enumerable: false,
    })
  }

  private _coerceScriptFieldValue(value: unknown): unknown {
    if (
      value != null &&
      typeof value === 'object' &&
      !(value instanceof UniDataTable) &&
      Array.isArray((value as { rows?: unknown }).rows)
    ) {
      return new UniDataTable(value as any)
    }
    return value
  }

  /**
   * Возвращает Proxy над UniData для совместимости с Rhino-скриптами:
   * - getField("name") → value с .size() (а не TypedValue)
   * - data["fieldName"] / data.fieldName → value с .size()
   * - остальные методы UniData работают как обычно
   */
  private _makeDataProxy(ud: UniData): any {
    const coerceFieldValue = (value: unknown) => this._coerceScriptFieldValue(value)
    return new Proxy(ud, {
      get(target: UniData, key: string | symbol) {
        if (typeof key === 'symbol') return (target as any)[key]
        const keyStr = String(key)
        const targetAny = target as any

        if (keyStr === 'getField') {
          return (name: string) =>
            UniData.wrapValueWithSize(coerceFieldValue(target.getField(name).value))
        }
        if (keyStr in targetAny && typeof targetAny[keyStr] === 'function') {
          return targetAny[keyStr].bind(target)
        }
        if (!target.hasField(keyStr)) {
          return targetAny[keyStr]
        }
        const field = target.getField(keyStr)
        if (field != null && field.value !== undefined) {
          return UniData.wrapValueWithSize(coerceFieldValue(field.value))
        }
        return targetAny[keyStr]
      },
    })
  }

  /** UniData proxy для platform imports (`putFullData().getField` → таблица с .addRow). */
  private _scriptUniData(): any {
    return this._makeDataProxy(this.data)
  }

  private _setupGlobalScope(): void {
    try {
      installPlatformScriptConsole()
      const enProxy = new Proxy(this, {
        get: (target, key) => {
          if (typeof key === 'symbol') return (target as any)[key]
          if (String(key) === 'getData') {
            return () => target._makeDataProxy(target.getData())
          }
          const val = (target as any)[String(key)]
          return typeof val === 'function' ? val.bind(target) : val
        }
      })
      ;(window as any).en = enProxy
      ;(window as any).data = this
      ;(window as any).UniDataTools = UniDataTools
      ;(window as any).UniData = UniData
      ;(window as any).UniDataTable = UniDataTable
      assignClientImportWindowGlobals(window)
      ;(window as any).Packages = Packages
      ;(window as any).SwingTreeTable = Packages.ru.esoft.swingfatclient.swinglogic.SwingTreeTable
      installJavaUtilShim()
      ;(window as any).Messages = this.controls?.Messages ?? messagesService
      ;(window as any).ProgressBar = ProgressBar
      ;(window as any).MainFrame = Packages.ru.esoft.swingfatclient.frames.MainFrame
      ;(Packages as any).ru = (Packages as any).ru || {}
      ;(Packages as any).ru.esoft = (Packages as any).ru.esoft || {}
      ;(Packages as any).ru.esoft.fatclient = (Packages as any).ru.esoft.fatclient || {}
      ;(Packages as any).ru.esoft.fatclient.tools = (Packages as any).ru.esoft.fatclient.tools || {}
      ;(Packages as any).ru.esoft.fatclient.tools.ProgressBar =
        (Packages as any).ru.esoft.fatclient.tools.ProgressBar || ProgressBar
      ;(Packages as any).ru.esoft.swingfatclient = (Packages as any).ru.esoft.swingfatclient || {}
      ;(Packages as any).ru.esoft.swingfatclient.frames =
        (Packages as any).ru.esoft.swingfatclient.frames || {}
      ;(Packages as any).ru.esoft.swingfatclient.frames.MainFrame =
        (Packages as any).ru.esoft.swingfatclient.frames.MainFrame ||
        Packages.ru.esoft.swingfatclient.frames.MainFrame
      ;(Packages as any).ru.esoft.wf = (Packages as any).ru.esoft.wf || {}
      ;(Packages as any).ru.esoft.wf.client = (Packages as any).ru.esoft.wf.client || {}
      ;(Packages as any).ru.esoft.wf.client.constants = (Packages as any).ru.esoft.wf.client.constants || {}
      ;(Packages as any).ru.esoft.wf.client.constants.LocalProperties =
        (window as any).LocalProperties || (Packages as any).ru.esoft.wf.client.constants.LocalProperties
      ;(window as any).convert = Convert
      ;(window as any).$ = this._getClientImport.bind(this)
      ;(window as any).importer = { get: this._getClientImport.bind(this) }
      ;(window as any).setTimeoutAsync = Packages.setTimeoutAsync
      ;(window as any).asyncLoop = Packages.asyncLoop
      // Stub for menu in_unidata scripts that start with "process "
      if ((window as any).process === undefined) {
        ;(window as any).process = {}
      }
      ;(window as any).isInstanceOf = platformIsInstanceOf
      ;(window as any).javaLength = javaLength

      const sel = this.actionData?.selected
      if (sel !== undefined && sel !== null) {
        ; (window as any).selected = sel
        const rawText = (typeof sel === 'object' && sel !== null && 'value' in sel) ? (sel as any).value : sel
        ; (window as any).text = wrapScriptValue(rawText == null ? '' : String(rawText))
      } else {
        ; (window as any).text = wrapScriptValue('')
      }
    } catch (error) {
      // Continue without global scope
    }

    try {
      Object.defineProperty(window, 'data', {
        get: () => this._makeDataProxy(this.getData()),
        configurable: true
      })
    } catch (error) {}
  }

  private _getClientImport(name: string): (instance?: any) => (...args: any[]) => any {
    return (...args: any[]) => {
      try {
        // Meta client-import uses Java URI/IDN (server-only). Prefer the browser port
        // over window[name]: sanitize-first compile can still hang a broken URI/IDN func there.
        if (name === 'extractProtocolAndDomain') {
          return extractProtocolAndDomain(
            String(args[0] ?? ''),
            String(args[1] ?? ''),
          )
        }
        // Thin-client each is async; Swing / formatPostalAddress expect sync iteration.
        if (name === 'each') {
          return platformEach(args[0], args[1] as (item: unknown, index: number) => unknown)
        }
        if (typeof (window as any)[name] === 'function') {
          const result = (window as any)[name](...args)
          return result
        }
        // webui platform identity for $("getClientType")() in form scripts (Swing: FatClient vs WebClient).
        if (name === 'getClientType') {
          return 'WebClient'
        }
        throw new MiddleExecutionRequiredError(name)
      } catch (error) {
        if (error instanceof MiddleExecutionRequiredError) {
          throw error
        }
        console.error(`Error executing client import ${name}:`, error)
        throw error
      }
    }
  }

  /**
   * Устанавливает данные действия (например, выбранные данные из таблицы)
   * @param actionData - данные действия, включая selected.fields
   */
  _setActionData(actionData: any): void {
    this.actionData = actionData
    const selected = actionData?.selected
    if (!selected || typeof selected !== 'object') return

    if (this.tilesRowScope) {
      // Action HTTP (27534): только PK. inlineAfterLoad поверх кладёт поля строки (statusId).
      const identity = pickTilesRowIdentityFields(selected)
      if (Object.keys(identity).length > 0) {
        this.data._select(identity)
      }
      return
    }

    if (selected.fields && typeof selected.fields === 'object') {
      this.data._select(selected.fields)
      return
    }

    // Плоская строка контекстного меню / TreeTable без вложенного fields.
    const flatFields = convertRowDataToFieldsForScript(selected)
    if (Object.keys(flatFields).length > 0) {
      this.data._select(flatFields)
    }
  }

  /**
   * Сбрасывает данные UniData и инициализирует новым объектом
   * @param obj - начальные данные для инициализации
   */
  _resetData(obj: Record<string, any> = {}): void {
    this.data = new UniData(obj)
    this.properties = {}
    this.actionData = {}
    this.reloadFormSelectedData = null
    this._putFullDataKeys.clear()
  }

  /**
   * Remember the element-action row for reloadForm only.
   * Do not put it on actionData: _setActionData overlays the row onto UniData
   * and putFormData would send that id to the procedure.
   */
  _setReloadFormSelectedData(selectedData: unknown): void {
    this.reloadFormSelectedData = selectedData ?? null
  }

  /** SwingForm.getData(events) collects nothing. The script En must still know that. */
  _setReloadFormActionLayout(actionData: unknown): void {
    this.reloadFormActionLayout = actionData ?? null
  }

  /**
   * Добавляет данные в текущий UniData объект
   * @param obj - объект с данными для добавления
   */
  _addData(obj: Record<string, any> = {}): void {
    this.data.add(obj)
  }

  /**
   * Добавляет данные ответа сервера в UniData
   * @param data - данные ответа сервера
   */
  _addResponse(data: Record<string, any>): void {
    this.data.add(data)
  }

  /**
   * Устанавливает идентификатор формы
   * @param id - идентификатор формы (число или строка)
   */
  _setFormId(id: number | string | undefined): void {
    if (id === undefined) {
      this.formId = -1
    } else if (typeof id === 'string') {
      this.formId = parseInt(id) || -1
    } else {
      this.formId = id
    }
  }

  /**
   * Устанавливает идентификатор панели
   * @param id - идентификатор панели (число или строка)
   */
  _setPanelId(id: number | string | undefined): void {
    if (id === undefined) {
      this.panelId = -1
    } else if (typeof id === 'string') {
      this.panelId = parseInt(id) || -1
    } else {
      this.panelId = id
    }
  }

  /**
   * Устанавливает идентификатор страницы формы (для putPageData).
   */
  _setPageId(id: number | string | undefined): void {
    if (id === undefined) {
      this.pageId = -1
    } else if (typeof id === 'string') {
      this.pageId = parseInt(id) || -1
    } else {
      this.pageId = id
    }
  }

  /**
   * Устанавливает данные страницы
   * @param data - данные страницы
   */
  _setPageData(data: any): void {
    this.pageData = data
  }

  _setPageDataSlotId(slotId: string | number | undefined | null): void {
    this.pageDataSlotId = slotId != null && String(slotId).trim() !== '' ? String(slotId) : ''
  }

  _setTilesRowScope(scope: SwingTilesRowScopeLike | null | undefined): void {
    this.tilesRowScope = scope?.tableFieldName != null && Number.isFinite(Number(scope.rowIndex))
      ? scope
      : null
  }

  _getTilesRowScope(): SwingTilesRowScopeLike | null {
    return this.tilesRowScope
  }

  _setServerEventCoreId(coreId: string | number | null | undefined): void {
    const normalized = coreId != null ? String(coreId).trim() : ''
    this.serverEventCoreId = normalized || null
  }

  _getServerEventCoreId(): string | null {
    return this.serverEventCoreId
  }

  /** Зафиксировать слот формы сразу после sync слушателя. */
  _pinServerEventForm(): void {
    this.serverEventFormPin = {
      pageDataSlotId: this.pageDataSlotId,
      formId: this.formId,
      panelId: this.panelId,
      pageData: this.pageData,
      sessionId: this._readPageSessionId(this.pageData),
    }
  }

  _clearServerEventFormPin(): void {
    this.serverEventFormPin = null
  }

  private _readPageSessionId(pageData: unknown): string | null {
    const raw = (pageData as { sessionId?: unknown } | null | undefined)?.sessionId
    if (raw == null) return null
    const text = String(raw).trim()
    return text && text !== '-1' ? text : null
  }

  private _pinnedServerEventSessionId(): string | null {
    return this.serverEventFormPin?.sessionId ?? null
  }

  private _pinnedServerEventSlotId(): string | undefined {
    const slot = String(this.serverEventFormPin?.pageDataSlotId ?? '').trim()
    return slot || undefined
  }

  private _reapplyServerEventFormPin(): void {
    const pin = this.serverEventFormPin
    if (!pin) return
    this.pageDataSlotId = pin.pageDataSlotId
    this.formId = pin.formId
    this.panelId = pin.panelId
    this.pageData = pin.pageData
  }

  private _serverEventVisibilityScope(): { formId?: string; panelId?: string } | undefined {
    if (!this.serverEventFormPin) return undefined
    const formId = this.formId !== -1 ? String(this.formId) : ''
    const panelId = this.panelId !== -1 ? String(this.panelId) : ''
    if (!formId && !panelId) return undefined
    return {
      ...(formId ? { formId } : {}),
      ...(panelId ? { panelId } : {}),
    }
  }

  getPageDataSlotId(): string {
    return this.pageDataSlotId
  }

  getFormId(): number {
    return this.formId
  }

  /**
   * После reloadElement / empty() скрипт читает таблицу через putFullData.
   * Синхронизируем поле из page store — getFullData может быть пустым до mount/reactivity.
   */
  private _mergeElementFieldFromPageStore(fieldName: string): boolean {
    const getter = this.controls?.getPageStoreField
    if (typeof getter !== 'function') {
      return false
    }
    const refresh = this.controls?.refreshPageDataSnapshot
    if (typeof refresh === 'function') {
      const pageData = refresh({
        formId: this.formId,
        pageDataSlotId: this.pageDataSlotId,
      })
      if (pageData) {
        this._setPageData(pageData)
      }
    }
    const field = getter(fieldName, {
      formId: this.formId,
      pageDataSlotId: this.pageDataSlotId,
    })
    if (!fieldEntryHasPresentableValue(field) && !fieldEntryIsUniDataTable(field)) {
      return false
    }
    this.data.setField(fieldName, field)
    return true
  }

  private async _mergeElementFieldFromPageStoreWithWait(
    fieldName: string,
    waitMs = 2000,
  ): Promise<boolean> {
    const deadline = Date.now() + Math.max(0, waitMs)
    do {
      if (this._mergeElementFieldFromPageStore(fieldName)) {
        return true
      }
      if (Date.now() >= deadline) {
        break
      }
      await new Promise((resolve) => setTimeout(resolve, 50))
    } while (true)
    return false
  }

  /**
   * Сериализует данные UniData в плоский объект для отправки на сервер
   * Пропускает поля с флагом doNotSerialize
   * @returns объект с полями формы (только значения, без типов)
   */
  _serialize(): Record<string, any> {
    // Get all fields from UniData using _getAllFields() method
    const allFields = this.data._getAllFields()
    const keys = Object.keys(allFields)
    const out: Record<string, any> = {}

    for (const key of keys) {
      if (this._getElements()[key]?.doNotSerialize) {
        continue
      }
      // Extract value from TypedValue format {value, type}
      const field = allFields[key]
      if (field && typeof field === 'object' && 'value' in field) {
        out[key] = field.value
      } else {
        out[key] = field
      }
    }

    return out
  }

  private _getOverlays(): Record<string, { name: string; overlayId: string; setVisible?: (v: boolean) => void; isVisible?: () => boolean }> {
    const ctrl = this.controls as { getOverlays?: () => Record<string, unknown> }
    const raw = ctrl?.getOverlays?.()
    return (raw && typeof raw === 'object' ? raw : {}) as Record<string, {
      name: string
      overlayId: string
      setVisible?: (v: boolean) => void
      isVisible?: () => boolean
    }>
  }

  private _getOverlayRefillCandidates(): Array<{ fieldName?: unknown; pageId?: unknown }> {
    const ctrl = this.controls as {
      getElementsByKey?: () => Record<string, { fieldName?: unknown; pageId?: unknown }>
    }
    const byKey = ctrl?.getElementsByKey?.()
    if (byKey && typeof byKey === 'object') {
      return Object.values(byKey)
    }
    return Object.values(this._getElements()).map((el) => ({
      fieldName: el.fieldName ?? el.name,
      pageId: el.pageId,
    }))
  }

  getRegistry() {
    return {
      getRegistryItemByName: (clazz: unknown, name: string) => {
        const element = this.getElement(name)
        if (!element) return undefined

        if (this._isSwingTreeTableClass(clazz)) {
          const type = String(element.elementType ?? (element as any).type ?? '').toUpperCase()
          return type === 'TREETABLE' ? element : undefined
        }

        return element
      },
    }
  }

  private _isSwingTreeTableClass(clazz: unknown): boolean {
    if (!clazz) return false
    if (clazz === Packages.ru.esoft.swingfatclient.swinglogic.SwingTreeTable) return true
    const asAny = clazz as { name?: unknown; className?: unknown; getName?: () => unknown }
    const name =
      typeof asAny.getName === 'function'
        ? asAny.getName()
        : asAny.className ?? asAny.name ?? String(clazz)
    return String(name).includes('SwingTreeTable')
  }

  private _tryGetEntity(name: string): Entity | undefined {
    return (
      this.getEntity('overlay', name) ||
      this.getEntity('element', name) ||
      this.getEntity('action', name) ||
      this.getEntity('panel', name) ||
      this.getEntity('form', name) ||
      (console.warn(`entity ${name} not found`), undefined)
    )
  }

  /* Функции клиентского кода, доступные из eval() */

  /**
   * Возвращает объект UniData с данными формы
   * @returns объект UniData для работы с данными формы
   */
  getData(): UniData {
    return this.data
  }

  /**
   * Возвращает свойства выполнения (isContextSet, isRepeatSet и т.д.)
   * @returns объект с свойствами выполнения
   */
  getProperties(): Record<string, any> {
    return this.properties
  }

  /**
   * Проверяет наличие поля в данных
   * @param name - имя поля
   * @returns true если поле существует
   */
  hasField(name: string): boolean {
    return this.data.hasField(name)
  }

  /**
   * Получает значение поля из данных
   * @param name - имя поля
   * @returns значение поля в формате TypedValue {value, type}
   */
  getField(name: string): any {
    return this.data.getField(name)
  }

  /**
   * Register a client function that can be imported via $('functionName').
   * If func is async, it is recorded in this.asyncImports for await-injection.
   */
  registerClientFunction(name: string, func: (...args: any[]) => any): void {
    const installed = name === 'each' ? platformEach : func
    ;(window as any)[name] = installed
    const isAsync =
      typeof installed === 'function' && (installed as any).constructor?.name === 'AsyncFunction'
    if (isAsync) {
      this.asyncImports[name] = true
    } else {
      delete this.asyncImports[name]
    }
  }

  /**
   * Unregister a client function
   */
  unregisterClientFunction(name: string): void {
    delete (window as any)[name]
    delete this.asyncImports[name]
  }

  /**
   * Получает объект панели с методами для работы с её элементами
   * @param name - id или jsName панели; без аргумента — текущая панель контекста
   * @returns объект панели с методом getSubentities() или undefined
   */
  getPanel(name?: string): any {
    if (!this.formId) {
      return
    }

    let panelId = this.panelId
    const trimmed = name != null ? String(name).trim() : ''
    if (trimmed !== '') {
      const numericId = parseInt(trimmed, 10)
      if (!Number.isNaN(numericId) && String(numericId) === trimmed) {
        panelId = numericId
      } else {
        const byJsName = resolvePanelIdByJsName(this.pageData, trimmed)
        if (byJsName != null) {
          panelId = byJsName
        } else {
          return
        }
      }
    }

    if (!panelId) {
      return
    }

    const form = this.controls.getForm({
      formId: this.formId,
      panelId,
      pageDataSlot: this.pageDataSlotId,
    })

    const requestFocus = (this.controls as { requestFocus?: (name: string) => void })?.requestFocus
    const resolvedPanelId = panelId
    const reloadTarget: string | number = trimmed !== '' ? trimmed : resolvedPanelId

    return {
      getSubentities: (): Entity[] => {
        const result: Entity[] = []
        for (const key of Object.keys(form)) {
          const ent = this._tryGetEntity(key)
          if (ent) result.push(ent)
        }
        return result
      },
      /**
       * фокусируем первый дочерний элемент панели
       */
      receiveFocus: (): void => {
        if (typeof requestFocus !== 'function') return
        for (const key of Object.keys(form)) {
          const ent = this._tryGetEntity(key)
          const focusName = (ent as { getName?: () => string })?.getName?.()
          if (focusName) {
            requestFocus(focusName)
            return
          }
          requestFocus(key)
          return
        }
        requestFocus(String(resolvedPanelId))
      },
      /**
       * Swing Panel.reload(UniData) — reload панели с текущими данными En.
       */
      reload: async (_data?: unknown): Promise<void> => {
        await this.reloadPanel(reloadTarget)
      },
    }
  }

  /**
   * Возвращает объект формы с минимальным Swing-совместимым API.
   */
  getForm(): any {
    if (!this.formId) {
      return
    }

    return {
      verify: () => {
        if (typeof this.controls?.verifyForm === 'function') {
          return this.controls.verifyForm(String(this.formId))
        }
        return true
      },
      invokeEvent: (eventName: string, data?: any) => {
        if (!eventName) {
          return
        }

        if (typeof this.controls?.runFormEvent === 'function') {
          this.controls.runFormEvent({
            formId: String(this.formId),
            eventName,
            data,
          })
          return
        }

        if (typeof this.controls?.runActionByName === 'function') {
          this.controls.runActionByName(eventName)
        }
      },
    }
  }

  /**
   * Получает класс элемента (не реализовано)
   */
  getClass(): void {
    console.warn('en.getClass() is not implemented')
  }

  /**
   * Очищает все данные формы
   */
  emptyData(): void {
    this._resetData()
  }

  private _finalizePutFullDataExport(
    trimmed: string,
    collected: Record<string, unknown>,
    element?: Entity,
  ): void {
    const elType = element
      ? String((element as Entity & { elementType?: string }).elementType ?? '').toUpperCase()
      : ''
    normalizeElementCollectedDataForExport(collected, {
      elementName: trimmed,
      elTypeNorm: elType,
      registered: element,
      fromElementSlot: collected,
      pageMerged: {},
    })
    const extraKeys =
      elType === 'COMBOBOX' && element
        ? parseComboBoxIdFieldPairsFromElement(element).map((p) => p.outKey)
        : []
    const pruned = prunePutFullDataCollectedFields(collected, trimmed, elType, {
      includeTilesRowNum: Boolean(this.tilesRowScope),
      extraKeys,
    })

    for (const [key, entry] of Object.entries(pruned)) {
      if (
        entry &&
        typeof entry === 'object' &&
        !Array.isArray(entry) &&
        'value' in (entry as object)
      ) {
        const typed = entry as { type?: string; value?: unknown }
        const plain = unwrapScriptValue(typed.value)
        if (plain !== typed.value) {
          pruned[key] = { ...typed, value: plain }
        }
      }
    }
    for (const key of Object.keys(pruned)) {
      this._putFullDataKeys.add(key)
    }
    this.data.add(pruned)
  }

  /**
   * `putFullData` уже положил строки; synth putFormData с пустым checkbox-selection
   * не должен заменить их пустой UniDataTable (форма 2675: Далее).
   */
  private _omitEmptyLeafSliceAfterPutFullData(
    payload: Record<string, unknown>,
  ): Record<string, unknown> {
    if (!this._putFullDataKeys.size) return payload
    let next: Record<string, unknown> | null = null
    for (const key of this._putFullDataKeys) {
      if (!Object.prototype.hasOwnProperty.call(payload, key)) continue
      if (!this.data.hasField(key)) continue
      const existing = this.data.getField(key)
      if (!fieldEntryIsUniDataTable(existing) || !fieldEntryHasPresentableValue(existing)) {
        continue
      }
      if (!fieldEntryLooksEmptyIncoming(payload[key])) continue
      if (!next) next = { ...payload }
      delete next[key]
    }
    return next ?? payload
  }

  /** `pageData.form` — определение формы для поиска FA вне registry. */
  private _resolveFormRootForFileAttachments(): unknown {
    const page = this.pageData as { form?: unknown } | null | undefined
    return page?.form ?? null
  }

  /** FILEATTACHMENTS из registry по fieldName, не только `elements[name]`. */
  private _resolveNamedFileAttachmentsEntity(name: string): Entity | undefined {
    const byKey =
      typeof this.controls?.formProvider?.getElementsByKey === 'function'
        ? (this.controls.formProvider.getElementsByKey() as Record<string, Entity>)
        : undefined
    const fromScan = resolveFileAttachmentsPutFullDataElement(
      [this._getElements() as Record<string, unknown>, byKey as Record<string, unknown> | undefined],
      name,
    ) as Entity | undefined
    return fromScan ?? this._getElements()[name]
  }

  /**
   * FILEATTACHMENTS: тип из registry или из form XML, если элемент уже unmount
   * (скрытая панель) и в `elements` его нет.
   */
  private _isFileAttachmentsField(name: string, element?: Entity): boolean {
    const elType = element
      ? String((element as Entity & { elementType?: string }).elementType ?? '').toUpperCase()
      : ''
    if (elType === 'FILEATTACHMENTS') return true
    return isFileAttachmentFieldInForm(this._resolveFormRootForFileAttachments(), name)
  }

  /**
   * После обычных источников putFullData (hook / getForm / page store):
   * если поле — FILEATTACHMENTS, берём реальное содержимое (в т.ч. со скрытой панели);
   * пустая UniDataTable — только когда данных действительно нет.
   */
  private _ensureFileAttachmentsPutFullData(name: string, element?: Entity): void {
    if (!this._isFileAttachmentsField(name, element)) return

    const fromData = this.data.hasField(name) ? this.data.getField(name) : undefined
    const fromPageStore =
      typeof this.controls?.getPageStoreField === 'function'
        ? this.controls.getPageStoreField(name, {
            formId: this.formId,
            pageDataSlotId: this.pageDataSlotId,
          })
        : undefined
    // Полный снимок формы включает FA со скрытых панелей (getForm overlay).
    const formSnapshot =
      typeof this.controls?.getForm === 'function'
        ? (this.controls.getForm({
            formId: element?.formId || this.formId,
            pageDataSlot: this.pageDataSlotId,
            tilesRowScope: this.tilesRowScope,
          }) as Record<string, unknown> | undefined)
        : undefined
    const fromForm =
      formSnapshot && typeof formSnapshot === 'object' ? formSnapshot[name] : undefined

    const preferred = pickPreferredFileAttachmentsField(fromData, fromForm, fromPageStore)
    if (
      preferred !== undefined &&
      (fieldEntryIsUniDataTable(preferred) || uniDataTableToFileItems(preferred).length > 0)
    ) {
      this.data.setField(
        name,
        fileItemsToUniDataTable(uniDataTableToFileItems(preferred)),
      )
      return
    }

    // Stub entity: finalize знает elType=FILEATTACHMENTS и добавит complex__element__name.
    const stub =
      element ??
      ({
        name,
        fieldName: name,
        elementType: 'FILEATTACHMENTS',
      } as Entity)
    this._finalizePutFullDataExport(name, { [name]: emptyFileAttachmentsUniDataTable() }, stub)
  }

  /**
   * Добавляет все данные элемента в UniData.
   * FILEATTACHMENTS: close+wait Р7 (`prepareForFormSave`) как у putFormData, затем getFullData.
   */
  putFullData(name: string): UniData | Promise<UniData> {
    const trimmed = String(name ?? '').trim()
    if (!trimmed) {
      return this._scriptUniData()
    }

    const elementEarly = this._resolveNamedFileAttachmentsEntity(trimmed)
    const elTypeEarly = elementEarly
      ? String((elementEarly as Entity & { elementType?: string }).elementType ?? '').toUpperCase()
      : ''
    const isFileAttachmentsField =
      elTypeEarly === 'FILEATTACHMENTS' || this._isFileAttachmentsField(trimmed, elementEarly)

    if (isFileAttachmentsField) {
      const pending = this._prepareFileAttachmentsForNamedPutFullData(elementEarly)
      if (pending) {
        return pending.then(() => this._collectPutFullData(trimmed))
      }
    }

    return this._collectPutFullData(trimmed)
  }

  private _collectPutFullData(name: string): UniData {
    const trimmed = name
    const nameKeyWidget = this._getElements()[trimmed]
    const resolvedWidget = this._resolveElementEntity(trimmed)
    const elementEarly = this._resolveNamedFileAttachmentsEntity(trimmed)
    const elTypeEarly = elementEarly
      ? String((elementEarly as Entity & { elementType?: string }).elementType ?? '').toUpperCase()
      : ''
    const isFileAttachmentsField =
      elTypeEarly === 'FILEATTACHMENTS' || this._isFileAttachmentsField(trimmed, elementEarly)

    // FILEATTACHMENTS: upload/promote только в хуке getFullData.
    // Нельзя early-return из page store — иначе локальный File / R7 staging не уходят на платформу.
    if (elementEarly && isFileAttachmentsField) {
      const elementData = this._resolveElementGetFullData(elementEarly)
      const elementFields = elementData._getAllFields()
      const presentable =
        this._scopedUniDataHasPresentableField(elementData, trimmed) ||
        this._scopedUniDataHasTableField(elementData, trimmed)
      if (presentable) {
        this._finalizePutFullDataExport(trimmed, elementFields, elementEarly)
        return this._scriptUniData()
      }
      this._ensureFileAttachmentsPutFullData(trimmed, elementEarly)
      return this._scriptUniData()
    }

    const element = elementEarly ?? resolvedWidget ?? nameKeyWidget
    const isMultiSelect = this._registeredElementType(element) === 'MULTISELECT'
    const knownLayoutField = collectLayoutFieldNames(
      this._resolveFormRootForFileAttachments(),
    ).has(trimmed)
    const currentEntry = this.data.hasField(trimmed) ? this.data.getField(trimmed) : undefined
    const isIdentificator =
      this._registeredElementType(element) === 'IDENTIFICATOR' ||
      isIdentificatorLayoutFieldName(this._resolveFormRootForFileAttachments(), trimmed)
    // Identificator (2921 pdTab*Visited) смонтирован только на открытой вкладке.
    // setField уже записал значение в En; снимок page store ещё false и не должен
    // его затирать. Поля другой вкладки в En нет — его читаем из store ниже.
    if (
      isIdentificator &&
      (fieldEntryHasPresentableValue(currentEntry) || fieldEntryIsUniDataTable(currentEntry))
    ) {
      return this._scriptUniData()
    }
    if (isIdentificator && element) {
      const elementData = this._resolveElementGetFullData(element)
      const elementFields = elementData._getAllFields()
      if (
        this._scopedUniDataHasPresentableField(elementData, trimmed) ||
        this._scopedUniDataHasTableField(elementData, trimmed)
      ) {
        this._finalizePutFullDataExport(trimmed, elementFields, element)
        return this._scriptUniData()
      }
    }
    // setField + putFullData на имени без элемента не должен затирать свежее
    // значение старым false из store. Незарегистрированный элемент без своего
    // значения по-прежнему читается из store.
    const keepScriptValue =
      !element &&
      !knownLayoutField &&
      (fieldEntryHasPresentableValue(currentEntry) || fieldEntryIsUniDataTable(currentEntry))

    if (isMultiSelect && element) {
      const nativeFull = element.getFullData
      const nativeData = element.getData
      const raw =
        typeof nativeFull === 'function'
          ? nativeFull.call(element)
          : typeof nativeData === 'function'
            ? nativeData.call(element)
            : null
      const scoped =
        raw != null ? scopeUniDataToElementField(raw, trimmed) : new UniData()
      if (this._scopedUniDataHasTableField(scoped, trimmed)) {
        this._finalizePutFullDataExport(trimmed, scoped._getAllFields(), element)
        return this._scriptUniData()
      }
      this._finalizePutFullDataExport(
        trimmed,
        { [trimmed]: toMultiSelectTypedValue([]) },
        element,
      )
      return this._scriptUniData()
    }

    if (
      !isMultiSelect &&
      !keepScriptValue &&
      this._mergeElementFieldFromPageStore(trimmed)
    ) {
      // FA (registry или form XML): не уходим в общий non-FA путь — нормализуем таблицу.
      if (!isFileAttachmentsField) {
        const collected: Record<string, unknown> = {}
        if (this.data.hasField(trimmed)) {
          collected[trimmed] = this.data.getField(trimmed)
        }
        this._finalizePutFullDataExport(trimmed, collected, elementEarly)
        return this._scriptUniData()
      }
      const merged = this.data.getField(trimmed)
      if (fieldEntryIsUniDataTable(merged)) {
        // Page store мог отдать таблицу без attributes/columns — выравниваем wire-shape.
        this.data.setField(
          trimmed,
          fileItemsToUniDataTable(uniDataTableToFileItems(merged)),
        )
        return this._scriptUniData()
      }
      this.data.removeField(trimmed)
    }

    if (element) {
      const elementData = this._resolveElementGetFullData(element)
      const elementFields = elementData._getAllFields()
      const presentable =
        this._scopedUniDataHasPresentableField(elementData, trimmed) ||
        this._scopedUniDataHasTableField(elementData, trimmed)
      if (presentable) {
        this._finalizePutFullDataExport(trimmed, elementFields, element)
        return this._scriptUniData()
      }

      const getFormArgs = {
        formId: element.formId || this.formId,
        panelId: element.panelId || this.panelId,
        elementId: element.ctx?.id || element.elementId,
        pageDataSlot: this.pageDataSlotId,
        tilesRowScope: this.tilesRowScope,
      }
      const target = this.controls.getForm(getFormArgs)

      if (target && typeof target === 'object' && Object.keys(target).length > 0) {
        const formScoped = new UniData()
        formScoped.add(target as Record<string, unknown>)
        const scopedElementData = scopeUniDataToElementField(formScoped, trimmed)
        if (
          this._scopedUniDataHasPresentableField(scopedElementData, trimmed) ||
          this._scopedUniDataHasTableField(scopedElementData, trimmed)
        ) {
          this._finalizePutFullDataExport(trimmed, scopedElementData._getAllFields(), element)
          return this._scriptUniData()
        }
      }

      const panelSlice = this._readPanelFormFieldSlice(trimmed, element)
      if (this._tryPutFullDataFromFormSlice(trimmed, panelSlice, element)) {
        // Slice мог не дать FA-таблицу (пустой/скрытый) — дожимаем empty UniDataTable.
        this._ensureFileAttachmentsPutFullData(trimmed, element)
        return this._scriptUniData()
      }
      this._ensureFileAttachmentsPutFullData(trimmed, element)
      return this._scriptUniData()
    }

    const panelSlice = this._readPanelFormFieldSlice(trimmed)
    if (this._tryPutFullDataFromFormSlice(trimmed, panelSlice, undefined)) {
      this._ensureFileAttachmentsPutFullData(trimmed, undefined)
      return this._scriptUniData()
    }

    // Элемент не в registry (скрытая панель) — тип берём из form XML.
    this._ensureFileAttachmentsPutFullData(trimmed, undefined)
    return this._scriptUniData()
  }

  /**
   * Аналог Swing `putData(...)`:
   * - собирает данные по scope (form/panel/element/page) или по имени поля;
   * - результат добавляет в `en.data`, чтобы последующие вызовы скрипта читали их через `en.getData()`;
   * - при `verify=true` добавляет служебный `__VERIFIED`.
   *
   * Используется, когда скрипту нужны данные конкретного элемента/области, а не всей формы.
   */
  putData(name: string, second?: any): UniData {
    const level = String(name ?? '').trim().toLowerCase()
    const levelId = typeof second === 'number' ? second : Number(second)
    if (
      (level === 'form' || level === 'panel' || level === 'element' || level === 'page') &&
      Number.isFinite(levelId)
    ) {
      const formId = level === 'form' ? levelId : this.formId
      const panelId = level === 'panel' ? levelId : this.panelId
      const elementId = level === 'element' ? levelId : undefined
      const target = this.controls.getForm({
        formId,
        panelId,
        elementId,
        flatten: true,
        pageDataSlot: this.pageDataSlotId,
      })
      if (target && typeof target === 'object' && !Array.isArray(target)) {
        // Swing putData(level,id) uses synthetic Action — leaf table getData.
        this.data.add(
          this._applySynthTableLeafCollectToPlainFields(target as Record<string, unknown>),
        )
      }
      this._mergeLiveFieldOverridesIntoData()
      return this.data
    }

    if (typeof second === 'boolean') {
      const result = this._putDataByElementName(String(name ?? ''))
      let verified = true
      if (second) {
        const element = this._resolveElementEntity(String(name ?? '').trim())
        if (element && typeof element.verify === 'function') {
          verified = element.verify()
        }
      }
      result.setField('__VERIFIED', { type: 'Boolean', value: verified })
      this.data.add(result)
      return result
    }

    return this._putDataByElementName(String(name ?? ''))
  }

  /**
   * Swing putData(String name): element.getData() → merge в context
   */
  private _putDataByElementName(name: string): UniData {
    const trimmed = String(name ?? '').trim()
    const result = new UniData()
    if (!trimmed) {
      return result
    }

    const element = this._resolveElementEntity(trimmed) ?? this._getElements()[trimmed]
    if (!element) {
      const formUd = this.getData()
      if (formUd.hasField(trimmed)) {
        result.setField(trimmed, formUd.getField(trimmed))
        this.data.add(result)
      }
      return result
    }

    if (isHiddenTableLikeElement(element, this._shellVisibleMap())) {
      const fieldName = String(element.fieldName ?? element.name ?? trimmed).trim()
      if (fieldName && this.data.hasField(fieldName)) {
        this.data.removeField(fieldName)
      }
      return result
    }

    const elementData = this._resolveElementGetData(element)
    if (elementData) {
      result.add(elementData)
    }

    const contextField = this.getData().hasField(trimmed) ? this.getData().getField(trimmed) : undefined
    const contextText = contextField != null
      ? new UniData({ [trimmed]: contextField }).getFieldAsString(trimmed, '')
      : ''
    if (contextText !== '' && result.getFieldAsString(trimmed, '') === '') {
      result.setField(trimmed, contextField)
    }

    this.data.add(result)
    return result
  }

  /**
   * Live TEXT/PASSWORD/TEXTAREA из UI поверх getForm (parity с putData(level, id)).
   * Нужен при skipStore: иначе putFormData/putPanelData теряют несохранённый ввод.
   */
  private _mergeLiveFieldOverridesIntoData(): void {
    if (typeof this.controls?.collectLiveFieldOverrides !== 'function') {
      return
    }
    let live = this.controls.collectLiveFieldOverrides(this.formId)
    if (live && typeof live === 'object' && this.tilesRowScope) {
      live = filterLiveOverridesToCurrentTilesRowPrefix(live, this.tilesRowScope)
    }
    if (live && typeof live === 'object' && Object.keys(live).length > 0) {
      const filtered: Record<string, unknown> = {}
      for (const [key, field] of Object.entries(live)) {
        const existing = this.data.hasField(key) ? this.data.getField(key) : undefined
        if (shouldPreserveFieldEntryOnEmptyIncoming(existing, field)) continue
        filtered[key] = field
      }
      if (Object.keys(filtered).length > 0) {
        this.data.add(this._omitEmptyIdentityIncomingOverPresentable(filtered))
      }
    }
  }

  /**
   * Inline table row (actionData.selected): после form.getData(synth) восстанавливаем identity
   * строки (_id, _typeId, …). Иначе putFormData затирает их document identity формы, а скрипты
   * вроде questId ← _id читают id родительского документа вместо выбранной строки.
   *
   * Tiles: PK-only поверх identity формы. Скрипт копирует PK плитки в отдельные поля,
   * затем сам возвращает identity мастера.
   */
  private _overlaySelectedRowIdentityAfterPutFormData(): void {
    if (this.tilesRowScope) {
      const rowData =
        typeof this.tilesRowScope.getRowData === 'function'
          ? this.tilesRowScope.getRowData()
          : undefined
      const collectedRowIdentity = pickTilesRowIdentityFromCollectedTable(
        this.data._getAllFields() as Record<string, unknown>,
        this.tilesRowScope,
      )
      const identity = overlayTilesRowIdentityOnFieldMap({}, [
        this.actionData?.selected,
        rowData,
        collectedRowIdentity,
      ])
      if (!fieldEntryHasPresentableValue(identity._id)) return
      this.data.add(identity)
      return
    }

    const selected = this.actionData?.selected
    if (!selected || typeof selected !== 'object') return

    const rowFields = convertRowDataToFieldsForScript(selected)
    const identity = pickDocumentIdentityFromSnapshot(rowFields)
    if (!identity._id) return

    this.data.add(identity)
  }

  /** Пустой incoming `_id`/`_typeId`/`_versionId` не затирает already-presentable seed. */
  private _omitEmptyIdentityIncomingOverPresentable(
    payload: Record<string, unknown>,
  ): Record<string, unknown> {
    const existing = this.data._getAllFields() as Record<string, unknown>
    const next = { ...payload }
    for (const key of ['_id', '_typeId', '_versionId'] as const) {
      if (!(key in next)) continue
      if (shouldPreserveFieldEntryOnEmptyIncoming(existing[key], next[key])) {
        delete next[key]
      }
    }
    return next
  }

  /**
   * Добавляет все данные текущей панели в UniData
   */
  putPanelData(): void {
    const { formId, panelId } = this
    const panelData = this.controls.getForm({
      formId,
      panelId,
      flatten: false,
      pageDataSlot: this.pageDataSlotId,
      tilesRowScope: this.tilesRowScope,
    })
    if (!panelData) {
      console.warn('Panel data not found!')
    }
    const panelFields =
      panelData && typeof panelData === 'object' && !Array.isArray(panelData)
        ? (panelData as Record<string, unknown>)
        : {}

    // Tiles: Swing putPanelData = clone panel.getData. Не dump UniDataTable-row
    // (statusId__listbox заказа, totalAmount, attachments) — ломает call&doc / metadata '-'.
    if (this.tilesRowScope) {
      const rowData =
        typeof this.tilesRowScope.getRowData === 'function'
          ? this.tilesRowScope.getRowData()
          : undefined
      const widgetNames = collectTilesCloneWidgetFieldNames(
        Object.values(this._getElements()),
        this.tilesRowScope,
        panelId,
      )
      const withRowFields = overlayTilesCloneWidgetFieldsFromRow(
        panelFields,
        this.tilesRowScope,
        widgetNames,
      )
      this.data.add(
        overlayTilesRowIdentityOnFieldMap(withRowFields, [this.actionData?.selected, rowData]),
      )
      this._mergeLiveFieldOverridesIntoData()
      return
    }

    const selectedFields =
      this.actionData?.selected &&
      typeof this.actionData.selected === 'object' &&
      !Array.isArray(this.actionData.selected) &&
      typeof this.actionData.selected.fields === 'object' &&
      this.actionData.selected.fields !== null
        ? (this.actionData.selected.fields as Record<string, unknown>)
        : null
    const payload =
      selectedFields && Object.keys(panelFields).length > 0
        ? { ...panelFields, ...selectedFields }
        : selectedFields ?? panelFields
    this.data.add(payload)
    this._mergeLiveFieldOverridesIntoData()
  }

  /**
   * Аналог Swing `putPageData()` на клиентском En.
   * Собирает данные панелей текущей страницы через controls.getPageData.
   */
  putPageData(): void {
    const { formId, pageId } = this
    let pageCollected: Record<string, unknown> | undefined

    if (
      typeof this.controls.getPageData === 'function' &&
      formId != null &&
      formId !== -1 &&
      pageId != null &&
      pageId !== -1
    ) {
      // Swing JSContext.putPageData → page.getData(action): все панели страницы.
      const fromPage = this.controls.getPageData({
        formId,
        pageId,
        flatten: true,
        pageDataSlot: this.pageDataSlotId,
        tilesRowScope: this.tilesRowScope,
      })
      if (fromPage && typeof fromPage === 'object' && !Array.isArray(fromPage)) {
        pageCollected = fromPage as Record<string, unknown>
      }
    }

    if (!pageCollected || Object.keys(pageCollected).length === 0) {
      // Fallback: снимок формы / page store (как putFormData), если getPageData пуст.
      const formFallback = this.controls.getForm?.({
        formId,
        pageDataSlot: this.pageDataSlotId,
        tilesRowScope: this.tilesRowScope,
      })
      pageCollected =
        formFallback && typeof formFallback === 'object' && !Array.isArray(formFallback)
          ? (formFallback as Record<string, unknown>)
          : {}
    }

    const selectedFields =
      this.actionData?.selected &&
      typeof this.actionData.selected === 'object' &&
      !Array.isArray(this.actionData.selected) &&
      typeof this.actionData.selected.fields === 'object' &&
      this.actionData.selected.fields !== null
        ? (this.actionData.selected.fields as Record<string, unknown>)
        : null
    if (!pageCollected || Object.keys(pageCollected).length === 0) {
      console.warn('Page data not found!')
    }
    const payload =
      selectedFields && pageCollected && typeof pageCollected === 'object'
        ? { ...pageCollected, ...selectedFields }
        : selectedFields ?? pageCollected
    this.data.add(payload)
    this._mergeLiveFieldOverridesIntoData()
  }

  /**
   * Аналог Swing `putFormData([verify])` на клиентском En.
   *
   * Добавляет в `en.data` снимок формы под правилами синтетического Action
   * (см. `dev/contracts/putFormData.md`): checkbox tables → checked rows; UniData.add поверх
   * уже узкого currentData (form identity перезаписывает одноимённые ключи).
   *
   * Важно: этот метод работает только на стороне клиентского `En` (локальный `en.run`).
   * Для серверного executeScript `putFormData` обрабатывается в JSContext (GlobalProvider).
   *
   * FILEATTACHMENTS webui-исключение: close Р7 + wait + getFullData upload (pending local / открытый редактор).
   * Без pending/prepare остаётся синхронным.
   */
  putFormData(): void | Promise<void> {
    const pending = this._prepareFileAttachmentsForPutFormData()
    if (pending) {
      return pending.then(() => {
        this._collectPutFormData()
      })
    }
    this._collectPutFormData()
  }

  private _listFileAttachmentsEntities(): Entity[] {
    return Object.values(this._getElements()).filter((el) => {
      if (String(el.elementType ?? '').toUpperCase() !== 'FILEATTACHMENTS') return false
      return this._registryElementIsCurrentForm(el)
    })
  }

  private _registryElementIsCurrentForm(element: Entity): boolean {
    const formId = String(element.formId ?? '').trim()
    const pageDataSlot = String((element as { pageDataSlot?: unknown }).pageDataSlot ?? '').trim()
    // Untagged registry entries are not a previous schema; FA tests and hooks rely on them.
    if (!formId && !pageDataSlot) return true
    return registryElementMatchesRequestedForm(
      {
        formId,
        pageDataSlot,
      },
      this.formId,
      this.pageDataSlotId,
    )
  }

  /** Widgets of the previous schema in this slot are not form.getData. */
  private _dropForeignRegistryFieldsFromData(): void {
    const currentNames = collectLayoutFieldNames(this._resolveFormRootForFileAttachments())
    for (const element of Object.values(this._getElements())) {
      if (!this._registryElementIsCurrentForm(element)) continue
      const name = String(element.fieldName ?? element.name ?? '').trim()
      if (name) currentNames.add(name)
    }
    const fields = this.data._getAllFields() as Record<string, unknown>
    for (const element of Object.values(this._getElements())) {
      if (this._registryElementIsCurrentForm(element)) continue
      const name = String(element.fieldName ?? element.name ?? '').trim()
      if (!name || currentNames.has(name)) continue
      if (Object.prototype.hasOwnProperty.call(fields, name)) {
        this.data.removeField(name)
      }
    }
  }

  private _currentFormElements(): Record<string, unknown> {
    const current: Record<string, unknown> = {}
    for (const [key, element] of Object.entries(this._getElements())) {
      if (!this._registryElementIsCurrentForm(element)) continue
      current[key] = element
    }
    return current
  }

  /** Close+wait на каждом FA с `prepareForFormSave` (inline/modal Р7). */
  private _prepareFileAttachmentsForPutFormData(): Promise<void> | null {
    const tasks: Promise<void>[] = []
    for (const entity of this._listFileAttachmentsEntities()) {
      const prepare = entity.prepareForFormSave
      if (typeof prepare !== 'function') continue
      const result = prepare.call(entity)
      if (result != null && typeof (result as Promise<void>).then === 'function') {
        tasks.push(result as Promise<void>)
      }
    }
    if (tasks.length === 0) return null
    return Promise.all(tasks).then(() => undefined)
  }

  /** Close+wait FA named in putFullData (entity prepare, or all FA if type is FILEATTACHMENTS). */
  private _prepareFileAttachmentsForNamedPutFullData(entity?: Entity): Promise<void> | null {
    const fromAll = this._prepareFileAttachmentsForPutFormData()
    if (fromAll) return fromAll
    if (!entity || typeof entity.prepareForFormSave !== 'function') return null
    const result = entity.prepareForFormSave.call(entity)
    if (result != null && typeof (result as Promise<void>).then === 'function') {
      return result as Promise<void>
    }
    return null
  }

  /**
   * Swing putFormData adds only form.getData. webui also forwards page context
   * scalars, but a script setField of a non-layout key (28051 serviceTypeId)
   * must survive that merge. Layout fields and _id/_typeId/_versionId still
   * come from the form, then the tiles PK overlay.
   */
  private _contextFieldsPutFormDataMustKeep(): Record<string, unknown> {
    const layoutNames = collectLayoutFieldNames(this._resolveFormRootForFileAttachments())
    for (const element of Object.values(this._getElements())) {
      if (!this._registryElementIsCurrentForm(element)) continue
      const name = String(element?.fieldName ?? element?.name ?? '').trim()
      if (name) layoutNames.add(name)
    }
    const current = this.data._getAllFields() as Record<string, unknown>
    const kept: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(current)) {
      if (isLayoutScopedFieldKey(key, layoutNames)) continue
      kept[key] =
        value && typeof value === 'object' ? { ...(value as Record<string, unknown>) } : value
    }
    return kept
  }

  /**
   * После close+wait: getFullData (upload File / promote staged), иначе в currentData останется code=-1.
   */
  private _overlayFileAttachmentsGetFullData(fields: Record<string, unknown>): void {
    for (const entity of this._listFileAttachmentsEntities()) {
      if (typeof entity.getFullData !== 'function') continue
      const fieldName = String(entity.fieldName ?? entity.name ?? '').trim()
      if (!fieldName) continue
      const elementData = this._resolveElementGetFullData(entity)
      const all = elementData._getAllFields() as Record<string, unknown>
      if (Object.prototype.hasOwnProperty.call(all, fieldName)) {
        fields[fieldName] = all[fieldName]
      } else {
        Object.assign(fields, all)
      }
    }
  }

  private _collectPutFormData(): void {
    this._dropForeignRegistryFieldsFromData()
    const keptContextFields = this._contextFieldsPutFormDataMustKeep()
    const { formId } = this
    const rawFormData = this.controls.getForm({
      formId,
      pageDataSlot: this.pageDataSlotId,
      tilesRowScope: this.tilesRowScope,
    })
    if (!rawFormData) {
      console.warn('Form data not found!')
    }

    // Swing: form.getData(synth) → data.add. Для inline table row identity строки восстанавливается
    // после снимка формы (_overlaySelectedRowIdentityAfterPutFormData).
    const formFields =
      rawFormData && typeof rawFormData === 'object' && !Array.isArray(rawFormData)
        ? (rawFormData as Record<string, unknown>)
        : {}

    const selections = resolveFormTableSelectionsForSynthCollect({
      formId,
      getTableSelections:
        typeof this.controls.getTableSelections === 'function'
          ? this.controls.getTableSelections.bind(this.controls)
          : typeof this.controls.formProvider?.getTableSelections === 'function'
            ? this.controls.formProvider.getTableSelections.bind(this.controls.formProvider)
            : undefined,
    })
    const payload = this._omitEmptyLeafSliceAfterPutFullData(
      applySynthActionTableLeafCollect(formFields, {
        elements: this._getElements() as Record<string, unknown>,
        extraMaps: this._elementLookupExtraMaps() as Array<Record<string, unknown> | undefined>,
        formId,
        selections,
        shellVisibleByElementId: this._shellVisibleMap(),
      }),
    )

    this.data.add(this._omitEmptyIdentityIncomingOverPresentable(payload))
    this._mergeLiveFieldOverridesIntoData()

    // getForm пропускает скрытые панели → FA может отсутствовать в snapshot.
    // Registry + form XML дополняют пустые UniDataTable для всех FileAttachments формы.
    const fields = this.data._getAllFields() as Record<string, unknown>
    applyFileAttachmentsToClientDataFieldsFromRegistry(
      fields,
      formFields,
      this._currentFormElements(),
      { formRoot: this._resolveFormRootForFileAttachments() },
    )
    this._overlayFileAttachmentsGetFullData(fields)
    // DateRange: развернуть {from,to} в name__first/__second (паритет SwingDateRange).
    applyDateRangeToClientDataFieldsFromRegistry(
      fields,
      formFields,
      this._getElements() as Record<string, unknown>,
    )
    applyFormIdentificatorAndListboxGetDataToFields(
      fields,
      this._getElements() as Record<string, unknown>,
      {
        formId,
        formRoot: this._resolveFormRootForFileAttachments(),
        extraMaps: this._elementLookupExtraMaps() as Array<Record<string, unknown> | undefined>,
      },
    )
    this.data.add(fields)
    this._mergeLiveFieldOverridesIntoData()
    this._overlaySelectedRowIdentityAfterPutFormData()
    if (Object.keys(keptContextFields).length > 0) {
      this.data.add(keptContextFields)
    }
  }

  /** Synth-collect tables for putData(form|…) — same Action rules as putFormData. */
  private _applySynthTableLeafCollectToPlainFields(
    fields: Record<string, unknown>,
  ): Record<string, unknown> {
    const selections = resolveFormTableSelectionsForSynthCollect({
      formId: this.formId,
      getTableSelections:
        typeof this.controls.getTableSelections === 'function'
          ? this.controls.getTableSelections.bind(this.controls)
          : typeof this.controls.formProvider?.getTableSelections === 'function'
            ? this.controls.formProvider.getTableSelections.bind(this.controls.formProvider)
            : undefined,
    })
    return applySynthActionTableLeafCollect(fields, {
      elements: this._getElements() as Record<string, unknown>,
      extraMaps: this._elementLookupExtraMaps() as Array<Record<string, unknown> | undefined>,
      formId: this.formId,
      selections,
      shellVisibleByElementId: this._shellVisibleMap(),
    })
  }

  private _shellVisibleMap(): Record<string, boolean> | { value?: Record<string, boolean> } | null {
    const controls = this.controls as {
      elementShellVisibleByElementId?: Record<string, boolean> | { value?: Record<string, boolean> }
      formProvider?: {
        elementShellVisibleByElementId?: Record<string, boolean> | { value?: Record<string, boolean> }
      }
    }
    return (
      controls?.elementShellVisibleByElementId ??
      controls?.formProvider?.elementShellVisibleByElementId ??
      null
    )
  }

  /**
   * Имя поля UniData по элементу
   */
  private _resolveElementFieldName(name: string | number): string {
    const element = this.getElement(name)
    const resolved = element
      ? String(element.fieldName ?? element.name ?? '').trim()
      : ''
    if (resolved) return resolved
    return typeof name === 'number' ? String(name) : String(name ?? '')
  }

  /** Swing refill/reload reads foregroundColor off the same UniData the script just wrote. */
  private _publishRefillForeground(fieldNames: Iterable<string>): void {
    for (const fieldName of fieldNames) {
      const raw = this.data.getFieldAttribute(fieldName, FOREGROUND_COLOR_ATTRIBUTE)
      if (raw == null) continue
      rememberFieldForeground(this.formId, fieldName, raw)
    }
  }

  /**
   * Обновляет значение элемента формы и вызывает его метод refill
   * @param name - имя или id элемента
   * @param data - данные для установки (опционально, в формате {value, type})
   */
  refillElement(name: string | number, data?: any): void {
    this._reapplyServerEventFormPin()
    const fieldName = this._resolveElementFieldName(name)
    const nameMapElement = resolveRegisteredElementByName(this._getElements(), name, this.formId)
    const refillByName = typeof name === 'string' && !/^\d+$/.test(String(name).trim())
    const targets = refillByName
      ? this._collectElementEntities(name)
      : (() => {
          const one = this._resolveElementEntity(name)
          return one ? [one] : []
        })()

    if (data !== undefined) {
      this.data.setField(fieldName, data)
    }

    const registeredForDefer = this._getOverlayRefillCandidates()
    const layoutDuplicatedFieldNames = (
      this.controls as { getOverlayDuplicatedFieldNames?: (formId?: string) => Iterable<string> }
    ).getOverlayDuplicatedFieldNames?.(
      this.formId != null && this.formId !== -1 ? String(this.formId) : undefined,
    )
    if (
      shouldDeferOverlayElementRefill(
        nameMapElement?.pageId,
        fieldName,
        registeredForDefer,
        layoutDuplicatedFieldNames,
      )
    ) {
      const queue = (this.controls as { queuePendingOverlayRefill?: (payload: {
        fieldName: string
        fieldData: unknown
        formId?: string
      }) => void }).queuePendingOverlayRefill
      if (typeof queue === 'function' && this.data.hasField(fieldName)) {
        const field = this.data.getField(fieldName)
        if (field.value !== undefined) {
          const fallbackFormId =
            this.formId != null && this.formId !== -1 ? String(this.formId) : undefined
          this._publishRefillForeground([fieldName])
          queue({ fieldName, fieldData: field, formId: fallbackFormId })
          return
        }
      }
    }

    const refillTargets = targets.filter((el) => typeof el.refill === 'function')
    if (refillTargets.length > 0) {
      const refillData: Record<string, any> = {}
      const suffixKeys = new Set<string>()
      for (const el of refillTargets) {
        for (const suf of el.getSuffixes?.() || ['']) {
          suffixKeys.add(fieldName + suf)
        }
      }
      if (suffixKeys.size === 0) suffixKeys.add(fieldName)
      for (const fieldKey of suffixKeys) {
        if (!this.data.hasField(fieldKey)) continue
        const field = this.data.getField(fieldKey)
        if (field.value !== undefined) {
          refillData[fieldKey] = field
        }
      }

      for (const el of refillTargets) {
        const refillFn = el.refill
        if (typeof refillFn === 'function') {
          refillFn.call(el, refillData)
        }
      }
      this._publishRefillForeground(suffixKeys)
      return
    }

    const hasRefillElement =
      this.controls && typeof (this.controls as any).refillElement === 'function'

    if (hasRefillElement && this.data.hasField(fieldName)) {
      const field = this.data.getField(fieldName)
      if (field.value !== undefined) {
        const fallbackFormId =
          this.formId != null && this.formId !== -1 ? String(this.formId) : undefined
        this._publishRefillForeground([fieldName])
        ;(this.controls as any).refillElement(fieldName, field, fallbackFormId)
      }
    }
  }

  /**
   * Перезагружает элемент формы с сервера
   * @param name - имя или id элемента
   * @returns объект UniData
   */
  async reloadElement(name: string | number): Promise<UniData> {
    this._reapplyServerEventFormPin()
    const fieldName = this._resolveElementFieldName(name)
    const element = this.getElement(name)
    const reloadOpts = {
      source: 'En.reloadElement',
      pageDataSlot: this.pageDataSlotId || undefined,
      formId: this.formId,
      skipPageFocusGuard: true,
      skipOnElement: true,
    }
    const slotAware = String(this.pageDataSlotId ?? '').trim() !== ''

    if (element && typeof element.reload === 'function') {
      try {
        const hasInvalidContext = this.formId === -1 || this.panelId === -1
        const currentValue = element.registeredValue as
          | { rows?: unknown; value?: { rows?: unknown } }
          | undefined
        const currentRows =
          Array.isArray(currentValue?.rows)
            ? currentValue.rows
            : Array.isArray(currentValue?.value?.rows)
              ? currentValue.value.rows
              : []
        // Slot-aware (dialogClosedNReturn on parent): do not skip HTTP — panelId is often unset.
        if (hasInvalidContext && currentRows.length > 0 && !slotAware) {
          startWorkflowMeasure('En.reloadElement.skipContextlessLoadedTable', {
            name,
            formId: this.formId,
            panelId: this.panelId,
            elementId: element.elementId,
            rows: currentRows.length,
          })()
          await this._mergeElementFieldFromPageStoreWithWait(fieldName)
          return this.data
        }

        await element.reload(this._serialize(), reloadOpts)
      } catch (error) {
        console.error(`Error reloading element ${name}:`, error)
      }
      await this._mergeElementFieldFromPageStoreWithWait(fieldName)
      return this.data
    }

    if (typeof this.controls?.reloadElement === 'function') {
      try {
        const pageData = this.pageData as { sessionId?: unknown } | null | undefined
        const eventCoreId = this._getServerEventCoreId()
        const pageSessionId =
          pageData?.sessionId != null ? String(pageData.sessionId).trim() : ''
        const validPageSessionId = pageSessionId && pageSessionId !== '-1' ? pageSessionId : ''

        await this.controls.reloadElement(fieldName, this._serialize(), {
          ...reloadOpts,
          source: this.pageDataSlotId
            ? 'En.reloadElement.slotAware'
            : 'En.reloadElement.controls',
          formPath: {
            formId: this.formId,
            panelId: this.panelId,
            pageDataSlot: this.pageDataSlotId || undefined,
            sessionId: this._pinnedServerEventSessionId() || validPageSessionId || undefined,
            pinnedSessionId: this._pinnedServerEventSessionId() || undefined,
            pinnedSlotId: this._pinnedServerEventSlotId(),
            eventCoreId: eventCoreId ?? undefined,
          },
        })
      } catch (error) {
        console.error(`Failed to reload element ${name} via controls:`, error)
      }
    }
    await this._mergeElementFieldFromPageStoreWithWait(fieldName)
    return this.data
  }

  /**
   * Перезагружает панель Swing parity:en.reloadPanel(jsName))
   * @param name - jsName или числовой id панели; без аргумента — текущая панель контекста
   */
  async reloadPanel(name?: string | number): Promise<void> {
    this._reapplyServerEventFormPin()
    const fromContext = this.panelId !== -1 ? this.panelId : ''
    const target =
      name != null && String(name).trim() !== '' ? name : fromContext
    if (typeof this.controls?.reloadPanel === 'function') {
      const pageData = this.pageData as { sessionId?: unknown; $parentPageId?: string } | null | undefined
      const eventCoreId = this._getServerEventCoreId()
      const pageSessionId =
        pageData?.sessionId != null ? String(pageData.sessionId).trim() : ''
      const validPageSessionId = pageSessionId && pageSessionId !== '-1' ? pageSessionId : ''

      // Если pageData.sessionId пуст, пытаемся получить sessionId из parent pageData или через readPlatformSessionIdFromPageEntry
      let effectiveSessionId = validPageSessionId
      if (!effectiveSessionId && this.controls?.getAppStore) {
        const appStore = this.controls.getAppStore()
        if (appStore?.pagesData) {
          // Сначала пробуем через readPlatformSessionIdFromPageEntry
          if (pageData) {
            const { readPlatformSessionIdFromPageEntry } = await import('@/common/utils/platformFileSession')
            const mainSessionId = readPlatformSessionIdFromPageEntry(pageData)
            if (mainSessionId) {
              effectiveSessionId = mainSessionId
            }
          }

          // Если не нашли, пробуем sessionId из parent pageData
          if (!effectiveSessionId) {
            const parentSlotId = pageData?.$parentPageId || '__main_frame__'
            const parentPageData = appStore.pagesData[parentSlotId] as { sessionId?: unknown } | undefined
            if (parentPageData?.sessionId) {
              const parentSessionId = String(parentPageData.sessionId).trim()
              if (parentSessionId && parentSessionId !== '-1') {
                effectiveSessionId = parentSessionId
              }
            }
          }
        }
      }

      const reloadPanel = this.controls.reloadPanel
      const result = await duringOwnedElementReload(() => reloadPanel(target, this._serialize(), {
        source: 'En.reloadPanel.slotAware',
        pageDataSlot: this.pageDataSlotId || undefined,
        formId: this.formId !== -1 ? String(this.formId) : undefined,
        panelId: this.panelId !== -1 ? String(this.panelId) : undefined,
        sessionId: this._pinnedServerEventSessionId() || effectiveSessionId || undefined,
        pinnedSessionId: this._pinnedServerEventSessionId() || undefined,
        pinnedSlotId: this._pinnedServerEventSlotId(),
        eventCoreId: eventCoreId ?? undefined,
        selectedData: this.reloadFormSelectedData ?? this.actionData?.selected,
      }))
      const fields = pickReloadFormFieldsFromResult(result)
      if (fields && Object.keys(fields).length > 0) {
        this.data.add(
          Object.fromEntries(
            Object.entries(fields).map(([key, value]) => [
              key,
              normalizeReloadPanelTableField(value),
            ]),
          ),
        )
        this._mergeLiveFieldOverridesIntoData()
      }

    } else {
      console.warn('en.reloadPanel: controls.reloadPanel is not available')
    }
  }

  /**
   * JSContext.reloadForm: POST `/platform/reload/form`, then overlay UniData (setData).
   */
  async reloadForm(): Promise<void> {
    this._reapplyServerEventFormPin()
    if (typeof this.controls?.reloadForm === 'function') {
      const pageData = this.pageData as { sessionId?: unknown } | null | undefined
      const eventCoreId = this._getServerEventCoreId()
      const pageSessionId =
        pageData?.sessionId != null ? String(pageData.sessionId).trim() : ''
      const eventsReload =
        isEventsLayoutAction(this.reloadFormActionLayout) || isEventsLayoutAction(this.actionData)
      const serialized = eventsReload ? eventsLayoutReloadFormFields(this.pageData) : this._serialize()
      const reloadForm = this.controls.reloadForm
      const result = await duringOwnedElementReload(() => reloadForm(serialized, {
        source: 'En.reloadForm.slotAware',
        pageDataSlot: this.pageDataSlotId || undefined,
        formId: this.formId !== -1 ? String(this.formId) : undefined,
        sessionId: this._pinnedServerEventSessionId() || pageSessionId || undefined,
        pinnedSessionId: this._pinnedServerEventSessionId() || undefined,
        pinnedSlotId: this._pinnedServerEventSlotId(),
        eventCoreId: eventCoreId ?? undefined,
        selectedData: this.reloadFormSelectedData ?? this.actionData?.selected,
        omitPageFieldSnapshot: eventsReload,
      }))
      const fields = pickReloadFormFieldsFromResult(result)
      if (fields && Object.keys(fields).length > 0) {
        this.data.replace(fields)
      }
    } else {
      console.warn('en.reloadForm: controls.reloadForm is not available')
    }
  }

  /**
   * Устанавливает фокус на элемент формы
   * @param name - имя элемента
   */
  requestFocus(name: string): void {
    const focusLayoutPage = (
      this.controls as { focusLayoutPage?: (pageName: string) => boolean } | undefined
    )?.focusLayoutPage
    if (typeof focusLayoutPage === 'function' && focusLayoutPage(String(name ?? ''))) {
      return
    }
    this.getElement(name)?.receiveFocus?.()
  }

  /**
   * Swing JSContext.scrollTo: прокрутка к элементу или панели по имени.
   * Виджет ещё не в registry — откладывается через controls.scrollToElement.
   */
  scrollTo(name: string | number): void {
    const trimmed = String(name ?? '').trim()
    if (!trimmed) return
    const element = this._resolveElementEntity(trimmed)
    if (typeof element?.scrollIntoViewElement === 'function') {
      element.scrollIntoViewElement()
      return
    }
    const scroll = (this.controls as {
      scrollToElement?: (elementName: string, formId?: number) => void
    })?.scrollToElement
    if (typeof scroll === 'function') {
      if (this.formId != null && this.formId !== -1) {
        scroll(trimmed, this.formId)
      } else {
        scroll(trimmed)
      }
    }
  }

  /**
   * Устанавливает флаг isContextSet, указывающий что контекст был установлен
   */
  setContext(): void {
    this.properties.isContextSet = true
  }

  /**
   * Opens a per-invocation context token.
   * The shared isContextSet flag is reset only when no other action is in flight.
   */
  openActionContext(): number {
    this.inFlightActionContexts += 1
    const token = ++this.actionContextSeq
    // A nested run (depth > 0) or a second in-flight action must not wipe the flag.
    if (this.scriptRunDepth === 0 && this.inFlightActionContexts === 1) {
      this.properties.isContextSet = false
    }
    return token
  }

  didSetContext(token: number): boolean {
    return this.contextSetTokens.has(token)
  }

  endActionContext(token: number): void {
    this.contextSetTokens.delete(token)
    this.inFlightActionContexts = Math.max(0, this.inFlightActionContexts - 1)
    if (this.contextSetTokens.size === 0) {
      this.properties.isContextSet = false
    }
  }

  /** setContext bound to the action that is executing this script, including after await. */
  setContextFor(token: number): void {
    this.properties.isContextSet = true
    if (Number.isFinite(token)) {
      this.contextSetTokens.add(token)
    }
  }

  private _rewriteSetContextCalls(source: string, token: number | undefined): string {
    if (token == null || !Number.isFinite(token)) return source
    return source.replace(/\ben\s*\.\s*setContext\s*\(\s*\)/g, `en.setContextFor(${token})`)
  }

  /**
   * Form-level error banner (Swing JSContext.setFormError / SwingForm.setError).
   * null / '' / whitespace clears the banner.
   */
  setFormError(message?: string | null): void {
    const text = normalizeFormErrorMessage(message)
    this.properties.formError = text
    const slot =
      this.pageDataSlotId ||
      (this.formId != null && this.formId !== -1 ? String(this.formId) : '')
    if (typeof this.controls?.setFormError === 'function') {
      this.controls.setFormError(text, slot || undefined)
    }
  }

  /**
   * Устанавливает флаг isRepeatSet, указывающий что нужно повторить операцию
   */
  setRepeat(): void {
    this.properties.isRepeatSet = true
  }

  /** Тип элемента для addListener: registry или form XML (onLoad до mount). */
  private _resolveElementTypeForListener(id: number | string): string | undefined {
    const entity = this.getEntity('element', String(id))
    if (entity) {
      const raw =
        (entity as Entity & { elementType?: string }).elementType ??
        (typeof entity.getType === 'function' ? entity.getType() : '')
      const normalized = String(raw ?? '').trim().toUpperCase()
      if (normalized) return normalized
    }
    return resolveFormElementTypeById(this._resolveFormRootForFileAttachments(), id)
  }

  /**
   * Регистрация client listener (аналог JSContext.addListener на middle).
   */
  addListener(
    level: string,
    id: number | string,
    eventName: string,
    fn: unknown,
    webClientExec?: boolean,
  ): void {
    const levelNorm = String(level ?? '').toLowerCase()
    const eventNorm = String(eventName ?? '').trim()
    if (
      levelNorm === 'element' &&
      eventNorm.toLowerCase() === TEXT_EDITED_LISTENER_EVENT.toLowerCase()
    ) {
      const elType = this._resolveElementTypeForListener(id)
      if (elType && !isPlainTextFieldType(elType)) {
        console.warn(
          `en.addListener: textEdited is only supported for Text elements, got ${elType} for element ${id}`,
        )
        return
      }
    }

    const numericId = typeof id === 'number' ? id : parseInt(String(id), 10)
    const listener = {
      level: String(level ?? '').toLowerCase(),
      id: Number.isFinite(numericId) ? numericId : id,
      event_name: eventName,
      function: typeof fn === 'function' ? fn.toString() : String(fn ?? ''),
      web_client_exec: webClientExec !== false,
      to_remove: false,
    }
    const add = (this.controls as { addFormEventListener?: (entry: typeof listener) => void })
      ?.addFormEventListener
    if (typeof add === 'function') {
      add(listener)
    }
  }

  /**
   * Локальный клиентский bus-event, аналог Swing windows.invokeEvent(...).
   */
  invokeBusEvent(eventName: string, data?: any): void {
    if (!eventName) {
      return
    }

    if (typeof this.controls?.invokeBusEvent === 'function') {
      this.controls.invokeBusEvent(eventName, data)
      return
    }

    console.warn('en.invokeBusEvent() is not implemented')
  }

  private _buildElementScopedUniDataFromForm(entity: Entity): UniData {
    const base = entity.fieldName || entity.name || ''
    if (!base) return new UniData()

    const formUd = this.getData()
    const scoped = new UniData()
    for (const key of Object.keys(formUd._getAllFields())) {
      if (isElementScopedFieldKey(key, base)) {
        scoped.setField(key, formUd.getField(key))
      }
    }
    return scoped
  }

  private _readElementFormSlice(entity: Entity): Record<string, unknown> | null {
    if (!this.controls?.getForm) {
      return null
    }
    const formId = entity.formId ?? this.formId
    const panelId = entity.panelId ?? this.panelId
    const elementId = entity.ctx?.id ?? entity.elementId
    if (formId == null || formId === -1 || panelId == null || elementId == null) {
      return null
    }
    const target = this.controls.getForm({
      formId,
      panelId,
      elementId,
      flatten: true,
      pageDataSlot: this.pageDataSlotId,
      tilesRowScope: this.tilesRowScope,
    })
    if (!target || typeof target !== 'object' || Array.isArray(target)) {
      return null
    }
    return target as Record<string, unknown>
  }

  private _scopedUniDataHasPresentableField(scoped: UniData, base: string): boolean {
    if (!base) {
      return Object.keys(scoped._getAllFields()).length > 0
    }
    if (scoped.hasField(base) && fieldEntryHasPresentableValue(scoped.getField(base))) {
      return true
    }
    for (const key of Object.keys(scoped._getAllFields())) {
      if (
        isElementScopedFieldKey(key, base) &&
        fieldEntryHasPresentableValue(scoped.getField(key))
      ) {
        return true
      }
    }
    return false
  }

  private _scopedUniDataHasTableField(scoped: UniData, base: string): boolean {
    if (!base) {
      return false
    }
    if (scoped.hasField(base) && fieldEntryIsUniDataTable(scoped.getField(base))) {
      return true
    }
    for (const key of Object.keys(scoped._getAllFields())) {
      if (
        isElementScopedFieldKey(key, base) &&
        fieldEntryIsUniDataTable(scoped.getField(key))
      ) {
        return true
      }
    }
    return false
  }

  private _resolveElementGetData(entity: Entity, action?: unknown): UniData {
    const base = entity.fieldName || entity.name || ''
    const native = entity.getData
    if (typeof native === 'function') {
      const raw = native.call(entity, action)
      const scoped = scopeUniDataToElementField(raw, base)
      if (
        this._scopedUniDataHasPresentableField(scoped, base) ||
        this._scopedUniDataHasTableField(scoped, base)
      ) {
        return scoped
      }
    }
    const formSlice = this._readElementFormSlice(entity)
    if (formSlice && Object.keys(formSlice).length > 0) {
      const formLevel = this._buildElementScopedUniDataFromForm(entity)
      const merged = mergeFieldMaps(formLevel._getAllFields(), formSlice)
      const scoped = new UniData()
      scoped.add(merged)
      const elementScoped = scopeUniDataToElementField(scoped, base)
      if (this._scopedUniDataHasPresentableField(elementScoped, base)) {
        return elementScoped
      }
    }
    return this._buildElementScopedUniDataFromForm(entity)
  }

  private _resolveElementGetFullData(entity: Entity): UniData {
    const base = entity.fieldName || entity.name || ''
    const native = entity.getFullData
    if (typeof native === 'function') {
      const raw = native.call(entity)
      const scoped = scopeUniDataToElementField(raw, base)
      if (
        this._scopedUniDataHasPresentableField(scoped, base) ||
        this._scopedUniDataHasTableField(scoped, base)
      ) {
        return scoped
      }
    }
    return this._resolveElementGetData(entity)
  }

  private _readPanelFormFieldSlice(
    fieldName: string,
    element?: Entity,
  ): Record<string, unknown> | null {
    if (typeof this.controls?.getForm !== 'function') {
      return null
    }
    const formId = element?.formId ?? this.formId
    const panelId = element?.panelId ?? this.panelId
    if (formId == null || formId === -1 || panelId == null || panelId === -1) {
      return null
    }
    const slice = this.controls.getForm({
      formId,
      panelId,
      pageDataSlot: this.pageDataSlotId,
      tilesRowScope: this.tilesRowScope,
      flatten: true,
    })
    if (!slice || typeof slice !== 'object' || Array.isArray(slice)) {
      return null
    }
    const entry = (slice as Record<string, unknown>)[fieldName]
    if (entry === undefined || entry === null) {
      return null
    }
    return { [fieldName]: entry }
  }

  private _tryPutFullDataFromFormSlice(
    fieldName: string,
    slice: Record<string, unknown> | null | undefined,
    element?: Entity,
  ): boolean {
    if (!slice || typeof slice !== 'object' || Object.keys(slice).length === 0) {
      return false
    }
    const formScoped = new UniData()
    formScoped.add(slice)
    const scopedElementData = scopeUniDataToElementField(formScoped, fieldName)
    if (
      !this._scopedUniDataHasPresentableField(scopedElementData, fieldName) &&
      !this._scopedUniDataHasTableField(scopedElementData, fieldName)
    ) {
      return false
    }
    this._finalizePutFullDataExport(fieldName, scopedElementData._getAllFields(), element)
    return true
  }

  /**
   * Добавляет getData() / getFullData() к зарегистрированному элементу (Vue-компонент), как в толстом клиенте.
   */
  private _wrapElementWithGetData(entity: Entity): Entity {
    return new Proxy(entity as object, {
      get: (target, prop, _receiver) => {
        if (prop === 'getData') {
          return (action?: unknown) => this._resolveElementGetData(target as Entity, action)
        }
        if (prop === 'getFullData') {
          return () => this._resolveElementGetFullData(target as Entity)
        }
        if (prop === 'getName') {
          return () => {
            const t = target as Entity
            return String(t.fieldName ?? t.name ?? '')
          }
        }
        if (prop === 'openSuggester') {
          return () => {
            const t = target as Entity
            if (typeof t.openSuggester === 'function') {
              t.openSuggester()
              return
            }
            const controls = this.controls as {
              openSuggesterElement?: (name: string, formId?: string) => void
            }
            const fieldName = String(t.fieldName ?? t.name ?? '').trim()
            if (!fieldName || typeof controls?.openSuggesterElement !== 'function') return
            const fid =
              t.formId != null && String(t.formId).trim() !== ''
                ? String(t.formId)
                : this.formId != null && this.formId !== -1
                  ? String(this.formId)
                  : undefined
            controls.openSuggesterElement(fieldName, fid)
          }
        }
        if (prop === 'getSelectedPrimaryKey') {
          return () => {
            const controls = this.controls as {
              resolveElementTableSwingMethod?: (
                entity: Entity,
                method: 'getSelectedPrimaryKey' | 'getPrimaryKeyColumns' | 'getType',
              ) => unknown
            }
            if (typeof controls?.resolveElementTableSwingMethod === 'function') {
              return controls.resolveElementTableSwingMethod(
                target as Entity,
                'getSelectedPrimaryKey',
              )
            }
            return new UniData({})
          }
        }
        if (prop === 'getPrimaryKeyColumns') {
          return () => {
            const controls = this.controls as {
              resolveElementTableSwingMethod?: (
                entity: Entity,
                method: 'getSelectedPrimaryKey' | 'getPrimaryKeyColumns' | 'getType',
              ) => unknown
            }
            if (typeof controls?.resolveElementTableSwingMethod === 'function') {
              return controls.resolveElementTableSwingMethod(
                target as Entity,
                'getPrimaryKeyColumns',
              )
            }
            return []
          }
        }
        if (prop === 'getType') {
          return () => {
            const controls = this.controls as {
              resolveElementTableSwingMethod?: (
                entity: Entity,
                method: 'getSelectedPrimaryKey' | 'getPrimaryKeyColumns' | 'getType',
              ) => unknown
            }
            if (typeof controls?.resolveElementTableSwingMethod === 'function') {
              const resolved = controls.resolveElementTableSwingMethod(target as Entity, 'getType')
              return resolved != null ? String(resolved) : ''
            }
            return String((target as Entity).elementType ?? '')
          }
        }
        if (prop === 'getPaginatorParams') {
          return () => {
            const t = target as Entity
            // If already registered, use existing method
            if (typeof (t as any).getPaginatorParams === 'function') {
              return (t as any).getPaginatorParams()
            }
            // Fallback: build paginator params manually (same logic as GlobalProvider.vue:1967-1989)
            const tableName = String(t.fieldName ?? t.name ?? '')
            if (!tableName) return {}
            const fid = t.formId != null ? String(t.formId) : this.formId != null ? String(this.formId) : ''
            if (!fid) return {}
            const pd = (this.controls as any)?.appStore?.pagesData?.[fid] ?? this.pageData
            if (!pd) return {}
            const params = (pd?.uniData?.clientData?.params || {}) as Record<string, any>
            const pageFields = pd?.uniData?.fields?.fields || {}
            const page = Number(params[`${tableName}__page`]) || 1
            const maxOnPage = Number(params[`${tableName}__maxOnPage`]) || 50
            const sizeFromParams = params[`${tableName}__size`]
            const sizeField = pageFields[`${tableName}__size`]
            const sizeFromFields =
              sizeField && typeof sizeField === 'object' && 'value' in sizeField
                ? sizeField.value
                : sizeField
            const tableField = pageFields[tableName]
            const rows = tableField?.value?.rows ?? tableField?.rows
            const rowsCount = Array.isArray(rows) ? rows.length : 0
            const totalSize = Number(sizeFromParams ?? sizeFromFields) || rowsCount
            const setType = (val: string) => ({ type: 'String', value: val })
            return {
              [`${tableName}__page`]: setType(String(page)),
              [`${tableName}__maxOnPage`]: setType(String(maxOnPage)),
              [`${tableName}__size`]: setType(String(totalSize)),
            }
          }
        }
        const val = (target as any)[prop as string]
        return typeof val === 'function' ? val.bind(target) : val
      },
    }) as Entity
  }

  /**
   * Получает элемент формы по имени или по числовому id элемента.
   * Для вызова по id возвращаем обёртку, которая делегирует invokeEvent актуальному элементу из _getElements()
   * Fallback: jsName/id панели (meta: en.getElement("messagesPanel").reload(...)).
   */
  getElement(name: string | number): Entity | undefined {
    const key = typeof name === 'number' ? String(name) : name
    let entity = this.getEntity('element', key) ?? this._getElements()[key]
    if (!entity) {
      const panel = this.getPanel(key)
      if (panel) return panel as Entity
      // Fallback: create virtual element for server imports that call getPaginatorParams before element registration
      return this._createVirtualElement(key)
    }
    if (typeof name === 'number' && /^\d+$/.test(key)) {
      const id = parseInt(key, 10)
      entity = this._wrapElementForInvoke(entity, id)
    }
    return this._wrapElementWithGetData(entity)
  }

  /**
   * Creates a virtual element stub for server imports (e.g., updateRegistry) that access
   * table elements before they are registered. Provides minimal methods like getPaginatorParams.
   */
  private _createVirtualElement(name: string): Entity {
    const virtualEntity: Entity = {
      name,
      fieldName: name,
      formId: this.formId != null ? String(this.formId) : undefined,
      setVisible: (visible) => this.setVisible(name, visible),
    }
    return this._wrapElementWithGetData(virtualEntity)
  }

  /**
   * Обёртка над элементом: invokeEvent вызывает runElementEvent из controls (прямой runAction),
   * чтобы обойти Vue Proxy при вызове из eval (onLoad-listeners).
   */
  private _wrapElementForInvoke(entity: Entity, elementId: number): Entity {
    const controls = this.controls as {
      runElementEvent?: (ctx: any) => Promise<unknown> | void
      trackPendingElementEvent?: (promise: Promise<unknown> | void) => void
    }
    return new Proxy(entity, {
      get: (target, prop: string, receiver) => {
        if (prop === 'invokeEvent') {
          return (eventName: string, data?: any) => {
            if (eventName === 'onChange' || eventName === 'onSelectionChanged') {
              if (controls.runElementEvent) {
                const el = target as any
                const promise = controls.runElementEvent({
                  elementId,
                  eventName,
                  data,
                  formId: el.formId != null ? String(el.formId) : undefined,
                  panelId: el.panelId != null ? String(el.panelId) : undefined,
                  name: el.fieldName ?? el.name,
                })
                controls.trackPendingElementEvent?.(promise)
                return promise
              }
            }
            const elMap = this._getElements()
            const current = Object.values(elMap).find((e: any) => e.elementId == elementId || e.ctx?.id == elementId)
            if (current?.invokeEvent) {
              return (current.invokeEvent as (e: string, d?: any) => void).call(current, eventName, data)
            }
          }
        }
        const val = (target as any)[prop]
        return typeof val === 'function' ? val.bind(target) : val
      },
    }) as Entity
  }

  /**
   * Получает сущность (элемент, действие, панель, форма) по типу и имени
   * @param type - тип сущности: 'element', 'action', 'panel', 'form'
   * @param name - имя или идентификатор сущности
   * @returns объект Entity или undefined
   */
  getEntity(type: string, name: string): Entity | undefined {
    switch (type) {
      case "action":
        return aliasActionSetEnabled(
          parseInt(name)
            ? this.actions[+name]
            : Object.values(this.actions).find((action) => action.name === name),
        )
      case "element": {
        return this._resolveElementEntity(name)
      }
      case "form":
        return this.getForm()
      case "panel":
        return this.getPanel(name)
      case "page": {
        const layout =
          (this.pageData as { form?: unknown } | undefined)?.form ?? this.pageData
        const match = findLayoutPageTabByVisibilityName(layout, name)
        if (!match) return undefined
        const applyEnabled = (enabled: boolean) => {
          const hook = (
            this.controls as {
              setPageEnabled?: (pageName: string | number, enabled: boolean) => void
            }
          )?.setPageEnabled
          if (typeof hook === 'function') hook(name, Boolean(enabled))
        }
        return {
          name: match.jsName || String(name),
          setEnabled: applyEnabled,
          setEnable: applyEnabled,
        } as Entity
      }
      case "overlay": {
        const overlays = this._getOverlays()
        const entry = overlays[name]
        if (entry) {
          return {
            name: entry.name,
            setVisible: (v: boolean) => entry.setVisible?.(v),
            isVisible: () => entry.isVisible?.() ?? false,
          } as Entity
        }
        const byId = Object.values(overlays).find(
          (o) => o.overlayId === name || o.name === name,
        )
        if (byId) {
          return {
            name: byId.name,
            setVisible: (v: boolean) => byId.setVisible?.(v),
            isVisible: () => byId.isVisible?.() ?? false,
          } as Entity
        }
        return undefined
      }
    }
    return undefined
  }

  /**
   * Очищает значение поля и обновляет элемент формы
   * @param name - имя поля/элемента
   */
  empty(name: string): void {
    const fieldName = this._resolveElementFieldName(name)
    if (typeof this.controls?.empty === 'function') {
      this.controls.empty(name)
    }
    if (this.data.hasField(fieldName)) {
      this.data.empty(fieldName)
    } else {
      this.data.setField(fieldName, emptyUniDataTableField().value)
    }
    if (typeof this.controls?.empty !== 'function' && this.data.hasField(fieldName)) {
      this.getElement(name)?.refill?.()
    }
  }

  /**
   * Client-only: queue visibility change (Swing ClientJSContext.dispatchVisibility).
   */
  dispatchVisibility(level: string, id: number, visible: boolean): void
  dispatchVisibility(element: string, visible: boolean): void
  dispatchVisibility(
    levelOrElement: string,
    idOrVisible: number | boolean,
    visible?: boolean,
  ): void {
    if (!this.properties.dispatchedVisibilities) {
      this.properties.dispatchedVisibilities = []
    }
    if (visible !== undefined && typeof idOrVisible === 'number') {
      this.properties.dispatchedVisibilities.push({
        level: levelOrElement,
        id: idOrVisible,
        visible,
      })
    } else {
      this.properties.dispatchedVisibilities.push({
        level: levelOrElement,
        id: null,
        visible: Boolean(idOrVisible),
      })
    }
  }

  /**
   * Устанавливает видимость элемента (и всех его дочерних элементов)
   * @param name - имя элемента, панели или формы
   * @param isVisible - флаг видимости
   */
  setVisible(name: string, isVisible: boolean): void
  setVisible(level: string, id: number | string, isVisible: boolean): void
  setVisible(
    nameOrLevel: string,
    idOrVisible: number | string | boolean,
    isVisible?: boolean,
  ): void {
    this._reapplyServerEventFormPin()
    const visibilityScope = this._serverEventVisibilityScope()
    if (isVisible !== undefined && typeof idOrVisible !== 'boolean') {
      if (!this.properties.dispatchedVisibilities) {
        this.properties.dispatchedVisibilities = []
      }
      this.properties.dispatchedVisibilities.push({
        level: nameOrLevel,
        id: idOrVisible,
        visible: isVisible,
      })
      if (typeof this.controls?.setVisibleByLevel === 'function') {
        this.controls.setVisibleByLevel(nameOrLevel, idOrVisible, isVisible)
        return
      }
      if (typeof this.controls?.setVisible === 'function') {
        if (visibilityScope) {
          this.controls.setVisible(String(idOrVisible), isVisible, visibilityScope)
        } else {
          this.controls.setVisible(String(idOrVisible), isVisible)
        }
        return
      }
      return
    }

    const name = nameOrLevel
    const visible = idOrVisible as boolean

    if (!this.properties.dispatchedVisibilities) {
      this.properties.dispatchedVisibilities = []
    }
    this.properties.dispatchedVisibilities.push({
      level: name,
      id: null,
      visible,
    })

    if (typeof this.controls?.setVisible === 'function') {
      if (visibilityScope) {
        this.controls.setVisible(name, visible, visibilityScope)
      } else {
        this.controls.setVisible(name, visible)
      }
      return
    }

    const ent = this._tryGetEntity(name)
    if (ent && ent.setVisible) {
      ent.setVisible(visible)

      // Recursively set visibility for all child elements (like Swing VisualEntity)
      // If this is a panel or form, propagate to all its elements
      this.propagateVisibilityToChildren(name, visible)
    }
  }

  /**
   * Устанавливает редактируемость (Swing JSContext: name,boolean | level,id,boolean).
   */
  setEditable(name: string, isEditable: boolean): void
  setEditable(level: string, id: number | string, isEditable: boolean): void
  setEditable(
    nameOrLevel: string,
    idOrEditable: number | string | boolean,
    isEditable?: boolean,
  ): void {
    if (isEditable !== undefined && typeof idOrEditable !== 'boolean') {
      if (!this.properties.dispatchedEditables) {
        this.properties.dispatchedEditables = []
      }
      this.properties.dispatchedEditables.push({
        level: nameOrLevel,
        id: idOrEditable,
        editable: isEditable,
      })
      if (typeof this.controls?.setEditableByLevel === 'function') {
        this.controls.setEditableByLevel(nameOrLevel, idOrEditable, isEditable)
        return
      }
      // Тесты без setEditableByLevel: 2-arg classify по id (панель не каскадит поле).
      if (typeof this.controls?.setEditable === 'function') {
        this.controls.setEditable(String(idOrEditable), isEditable)
        return
      }
      return
    }

    const name = nameOrLevel
    const editable = idOrEditable as boolean

    if (!this.properties.dispatchedEditables) {
      this.properties.dispatchedEditables = []
    }
    this.properties.dispatchedEditables.push({
      level: name,
      id: null,
      editable,
    })

    // formProvider.setEditable = setEditableByName (guard + leaf). Не setEditableRecursive.
    if (typeof this.controls?.setEditable === 'function') {
      this.controls.setEditable(name, editable)
      return
    }

    // Без formProvider (тесты / synth): тот же changed-guard, что в GlobalProvider. Поле — без каскада.
    const form = (this.pageData as { form?: unknown } | undefined)?.form ?? this.pageData
    const target = classifySetEditableName(name, { form, formId: this.formId })
    if (target.kind === 'panel' || target.kind === 'form') {
      const key = target.kind === 'panel' ? target.panelId : target.formId
      const xmlDefault =
        target.kind === 'panel' ? resolvePanelXmlDefaultEditable(form, key) : true
      const current = Object.prototype.hasOwnProperty.call(this.containerEditableById, key)
        ? this.containerEditableById[key]
        : undefined
      if (!shouldCascadePanelEditable(current, editable, xmlDefault)) {
        this.containerEditableById[key] = editable
        return
      }
      this.containerEditableById[key] = editable
      this.propagateEditableToChildren(key, editable)
      return
    }

    const element = this._tryGetEntity(name)
    if (element?.setEditable) {
      element.setEditable(editable)
    }
  }

  private propagateVisibilityToChildren(parentName: string, isVisible: boolean): void {
    // Find all elements that belong to this parent (panel or form)
    for (const [elementName, element] of Object.entries(this._getElements())) {
      // Check if element belongs to this parent
      // For panels: element.panelId should match
      // For forms: element.formId should match
      const belongsToParent =
        element.panelId === parentName ||
        element.formId === parentName ||
        String(element.schemaFormId ?? '') === String(parentName) ||
        (element.ctx?.id && element.ctx.id === parentName)

      if (belongsToParent && element.setVisible) {
        element.setVisible(isVisible)
      }
    }
  }

  private propagateEditableToChildren(parentName: string, isEditable: boolean): void {
    // Find all elements that belong to this parent (panel or form)
    for (const [elementName, element] of Object.entries(this._getElements())) {
      // Check if element belongs to this parent
      const belongsToParent =
        element.panelId === parentName ||
        element.formId === parentName ||
        String(element.schemaFormId ?? '') === String(parentName) ||
        (element.ctx?.id && element.ctx.id === parentName)

      if (belongsToParent && element.setEditable) {
        element.setEditable(isEditable)
      }
    }
  }

  setClose(): void {
    const p = this.getProperties()
    p.close__after__complete = true
    p.return__from__dialog = false
  }

  setReturn(): void {
    const p = this.getProperties()
    p.return__from__dialog = true
    p.close__after__complete = false
  }

  /**
   * Открывает URL в новом окне браузера
   * @param url - URL для открытия
   */
  openURL(url: string): void {
    window.open(url)
  }

  alert(message: any): void {
    reportPlatformScriptAlert(message)
  }

  /**
   * Downloads a file from the server
   * @param typeId - file type id
   * @param fileId - file id
   * @param versionId - file version
   * @param fileName - file name
   * @param hash - file hash
   * @returns Promise Blob or null if download fails
   */
  downloadFile(
    typeId: string,
    fileId: number,
    versionId: number,
    fileName: string,
    hash: string,
  ): Promise<Blob | null> {
    // TODO: уточнить данные по индексу formId или нет? в pageData
    const formPageData = this.pageData?.[this.formId] ?? this.pageData
    const sessionId = formPageData?.sessionId

    return fileService.downloadFile({
      type: typeId,
      code: fileId,
      version: versionId,
      hash,
      fileName,
      elementId: this.elementId,
      sessionId: sessionId || '',
    }).then((result) => result?.blob ?? null)
  }
}

declare global {
  interface String {
    isEmpty(): boolean
    equals(str: string): boolean
  }
}
